import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CategoryMultiSelect from '../../components/CategoryMultiSelect';
import FormModal from '../../components/ui/FormModal';
import Modal from '../../components/ui/Modal';
import SearchBar from '../../components/ui/SearchBar';
import StatusMessage from '../../components/ui/StatusMessage';
import VariantSpecsPills from '../../components/ui/VariantSpecsPills';
import { useAuth } from '../../context/AuthContext';
import {
  addProductSupplier,
  createVariant,
  createProductComponent,
  createOrUpdateProductSupplier,
  DEFAULT_PRICING_METHOD,
  deleteProductSupplier,
  deleteVariant,
  getMixedProductCalculationModeLabel,
  getMixedProductCalculationModeRequiredInputs,
  getMixedProductRequiredInputLabel,
  getComponentPriceHistory,
  getComponentSuppliers,
  getCategories,
  getActiveProductVariantsByProductId,
  getLatestPriceEntry,
  getPricingMethodLabel,
  getProductById,
  getProductComponents,
  getProducts,
  getProductSuppliers,
  getSuppliers,
  getSuppliersByIds,
  getVariantPriceHistory,
  MIXED_PRODUCT_CALCULATION_MODE_OPTIONS,
  MIXED_PRODUCT_REQUIRED_INPUT_OPTIONS,
  normalizeMixedProductCalculationMode,
  normalizeMixedProductRequiredInputs,
  normalizeEditablePricingMethod,
  normalizePricingMethod,
  normalizeVariantNames,
  PRICING_METHOD_OPTIONS,
  setVariantPrice,
  softDeleteProductComponent,
  sortPriceHistoryByDateDesc,
  updateSelectedComponentSupplier,
  updateProduct,
  updateProductComponent,
} from '../../services/firestoreService';
import {
  buildLookupMap,
  getDisplayValues,
  normalizeIdArray,
} from '../../utils/collectionUtils';
import {
  createBundle,
  createBundleExtraCost,
  deleteBundle,
  getBundleComponents,
  getBundleExtraCosts,
  getBundles,
  restoreBundle,
  softDeleteBundle,
  softDeleteBundleExtraCost,
  updateBundle,
  updateBundleExtraCost,
} from '../../services/bundleArchitectureService';
import {
  buildPricingSuppliersById,
  buildProductSupplierMap,
  buildSupplierPricingRow as buildSupplierLookupRow,
  getLinkedSupplierIds,
  getSupplierPriceDisplay,
  getSupplierIdForVariant,
} from './productDetailsSupplierLookup';
import {
  calculateMixedProductComponents,
  calculateMixedProductTotal,
  calculateComponentPricing,
  findConfigurationByDimensions,
  getComponentCalculationBreakdown,
  getConfigurationDimensionFields,
  getConfigurationDimensionKey,
  hasValidComponentSupplierPrice,
  normalizeConfigurationDimensions,
  suggestConfigurationName,
  validateConfigurationDimensions,
} from './mixedProductCalculations';

const productTypeOptions = [
  { value: 'simple', label: 'Simple' },
  { value: 'variant', label: 'Has Variants' },
  { value: 'mixed', label: 'Mixed' },
];

const getProductTypeLabel = (type) =>
  productTypeOptions.find((option) => option.value === type)?.label || 'Simple';

const getProductTypeDescription = (type, allowedVariantCount) => {
  if (type === 'variant') {
    return `${allowedVariantCount} allowed variant ${
      allowedVariantCount === 1 ? 'option' : 'options'
    }`;
  }

  if (type === 'mixed') {
    return 'Product assembled from reusable components';
  }

  return 'Single product without variant options';
};

const getDimensionFieldLabel = (fieldName) =>
  fieldName.charAt(0).toUpperCase() + fieldName.slice(1);

const normalizeEditableProductType = (type) =>
  ['simple', 'variant', 'mixed'].includes(type) ? type : 'simple';

const getRequiredInputLabels = (requiredInputs) =>
  normalizeMixedProductRequiredInputs(requiredInputs)
    .map((inputValue) => getMixedProductRequiredInputLabel(inputValue))
    .filter(Boolean);

const toggleRequiredInput = (currentInputs, inputValue) => {
  const selectedInputs = normalizeMixedProductRequiredInputs(currentInputs);

  return selectedInputs.includes(inputValue)
    ? selectedInputs.filter((item) => item !== inputValue)
    : [...selectedInputs, inputValue];
};

const createEmptyComponentForm = (productId = '') => ({
  productId,
  sourceProductId: '',
  sourceProductName: '',
  sourceVariantId: '',
  sourceVariantName: '',
  name: '',
  description: '',
  quantity: '1',
  unit: '',
  sortOrder: '0',
  isRequired: true,
});

const createEmptyExpenseForm = () => ({
  name: '',
  amount: '',
});

const getWorkingExpenseBundleId = (productId = '') => productId || '';

const calculateExpenseTotal = (expenses = []) =>
  (Array.isArray(expenses) ? expenses : []).reduce((total, expense) => {
    const amount = Number(expense?.amount);

    return Number.isFinite(amount) ? total + amount : total;
  }, 0);

const createDimensionFormValues = (dimensions = {}) => ({
  width: getDimensionInputValue(dimensions, 'width'),
  height: getDimensionInputValue(dimensions, 'height'),
  length: getDimensionInputValue(dimensions, 'length'),
});

const createEmptyConfigurationForm = (product = {}) => ({
  name: '',
  categoryId: normalizeIdArray(product?.categories)[0] || '',
  dimensions: createDimensionFormValues(),
});

const pricingSortOptions = [
  { value: 'lowest', label: 'Lowest Price' },
  { value: 'highest', label: 'Highest Price' },
  { value: 'latest', label: 'Latest Update' },
  { value: 'supplier', label: 'Supplier Name' },
];

const componentStatusLabels = {
  deleted: 'Deleted',
  needs_supplier: 'Needs supplier',
  needs_price: 'Needs price',
  needs_selection: 'Needs selection',
  optional_unselected: 'Optional unselected',
  ready: 'Ready',
};

const CONFIGURATION_SNAPSHOT_VERSION = 1;

const formatPrice = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return '-';
  }

  const numericValue = Number(value);

  if (Number.isFinite(numericValue)) {
    return `$${numericValue.toFixed(2)}`;
  }

  return '-';
};

const formatPriceWithPricingMethod = (formattedPrice, pricingMethod) =>
  `${formattedPrice} / ${getPricingMethodLabel(pricingMethod)}`;

const formatComponentPricingBreakdown = (breakdown = {}) => {
  if (
    breakdown.lineTotal === null ||
    breakdown.rate === null ||
    breakdown.quantity === null
  ) {
    return '-';
  }

  if (breakdown.pricingMethod === 'perSquareMeter') {
    return `${formatPrice(breakdown.rate)} / m2 | Area: ${formatDimensionValue(
      breakdown.quantity
    )} m2 | Total: ${formatPrice(breakdown.lineTotal)}`;
  }

  if (breakdown.pricingMethod === 'perUnit') {
    return `${formatPrice(breakdown.rate)} per unit | Qty: ${formatDimensionValue(
      breakdown.quantity
    )} | Total: ${formatPrice(breakdown.lineTotal)}`;
  }

  return 'Legacy pricing type - review required';
};

const parseDateValue = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate =
    typeof value?.toDate === 'function'
      ? value.toDate()
      : value instanceof Date
        ? value
        : new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

const formatDate = (value) => {
  const parsedDate = parseDateValue(value);

  if (!parsedDate) {
    return '-';
  }

  return parsedDate.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const normalizeHistoryNote = (value) =>
  typeof value === 'string' && value.trim() ? value.trim() : '-';

const getSafePriceHistory = (priceHistory) =>
  Array.isArray(priceHistory) ? priceHistory : [];

const getVariantName = (variant) =>
  variant?.variantName || variant?.name || '';

const getVariantNames = (variants) => {
  const seenNames = new Set();

  return (Array.isArray(variants) ? variants : []).reduce((names, variant) => {
    const variantName = getVariantName(variant);

    if (variantName && !seenNames.has(variantName)) {
      seenNames.add(variantName);
      names.push(variantName);
    }

    return names;
  }, []);
};

const getSelectableVariantNames = (product, variants) => {
  const allowedVariants = normalizeVariantNames(product?.allowedVariants);

  if (allowedVariants.length > 0) {
    return allowedVariants;
  }

  return getVariantNames(variants);
};

const getVariantSelectionOptions = (sourceProduct = {}, variants = []) => {
  if (sourceProduct?.type !== 'variant') {
    return [];
  }

  const variantNames = getSelectableVariantNames(sourceProduct, variants);

  return variantNames.map((variantName) => {
    const matchingVariant = (Array.isArray(variants) ? variants : []).find(
      (variant) => getVariantName(variant) === variantName
    );

    return {
      id: matchingVariant?.id || variantName,
      name: variantName,
    };
  });
};

const buildSupplierPricingRow = (productSupplier, supplierMap) => {
  const supplierLookupRow = buildSupplierLookupRow(productSupplier, supplierMap);

  if (!supplierLookupRow) {
    return null;
  }

  const { supplierId, supplierName, isTrashed } = supplierLookupRow;
  const latestPriceEntry = getLatestPriceEntry(productSupplier?.priceHistory);
  const hasPriceHistory = getSafePriceHistory(productSupplier?.priceHistory).length > 0;
  const pricingMethod = normalizePricingMethod(productSupplier?.pricingMethod);
  const latestPriceValue = Number(latestPriceEntry?.price);
  const latestUpdate = parseDateValue(latestPriceEntry?.date);

  return {
    id: productSupplier?.id || `${supplierId}-supplier-price`,
    supplierId,
    supplierName,
    isTrashed,
    isMissingSupplier: supplierLookupRow.isMissingSupplier === true,
    pricingMethod,
    pricingMethodLabel: getPricingMethodLabel(pricingMethod),
    latestPriceValue: latestPriceEntry?.price ?? '',
    latestPriceNote: latestPriceEntry?.note || latestPriceEntry?.notes || '',
    priceValue: hasPriceHistory && Number.isFinite(latestPriceValue)
      ? latestPriceValue
      : null,
    lastUpdatedTime: latestUpdate ? latestUpdate.getTime() : null,
    price: formatPriceWithPricingMethod(
      getSupplierPriceDisplay(
        hasPriceHistory,
        formatPrice(latestPriceEntry?.price)
      ),
      pricingMethod
    ),
    date: hasPriceHistory ? formatDate(latestPriceEntry?.date) : '—',
    note: hasPriceHistory ? normalizeHistoryNote(latestPriceEntry?.note) : '—',
    history: sortPriceHistoryByDateDesc(getSafePriceHistory(productSupplier?.priceHistory)),
    hasPriceHistory,
  };
};

const buildVariantPricingRow = (
  variant,
  supplierMap,
  productSupplierMap
) => {
  const latestPriceEntry = getLatestPriceEntry(variant?.priceHistory);
  const priceValue = Number(variant?.currentPrice);
  const latestUpdate = parseDateValue(variant?.lastUpdated);
  const supplierId = getSupplierIdForVariant(variant, productSupplierMap);
  const supplierLookupRow = buildSupplierLookupRow({ supplierId }, supplierMap);
  const variantProductSupplier =
    productSupplierMap[variant?.productSupplierId] ||
    Object.values(productSupplierMap || {}).find(
      (productSupplier) => productSupplier?.supplierId === supplierId
    );
  const pricingMethod = normalizePricingMethod(
    variantProductSupplier?.pricingMethod
  );

  if (!supplierId) {
    return null;
  }

  if (!supplierLookupRow) {
    return null;
  }

  return {
    id: variant?.id || `${supplierId}-${getVariantName(variant)}`,
    supplierId,
    supplierName: supplierLookupRow.supplierName,
    isTrashed: supplierLookupRow.isTrashed,
    isMissingSupplier: supplierLookupRow.isMissingSupplier === true,
    pricingMethod,
    pricingMethodLabel: getPricingMethodLabel(pricingMethod),
    variantId: variant?.id || '',
    productSupplierId: variantProductSupplier?.id || variant?.productSupplierId || '',
    variantName: getVariantName(variant),
    latestPriceValue: variant?.currentPrice ?? '',
    latestPriceNote: variant?.notes || latestPriceEntry?.note || '',
    price: variant
      ? formatPriceWithPricingMethod(formatPrice(variant.currentPrice), pricingMethod)
      : '-',
    priceValue: variant && Number.isFinite(priceValue) ? priceValue : null,
    date: variant ? formatDate(variant.lastUpdated) : '-',
    lastUpdatedTime: latestUpdate ? latestUpdate.getTime() : null,
    note: variant ? normalizeHistoryNote(variant.notes || latestPriceEntry?.note) : '-',
    specs: variant?.specs || {},
    hasVariant: Boolean(variant && supplierId),
  };
};

const getLatestVariantPricingForSupplier = (
  supplierId,
  variants = [],
  productSupplierMap = {}
) => {
  const supplierVariants = (Array.isArray(variants) ? variants : []).filter(
    (variant) => getSupplierIdForVariant(variant, productSupplierMap) === supplierId
  );
  const latestVariant = supplierVariants.reduce((latest, variant) => {
    const variantTime = getTimestampMs(variant?.lastUpdated);
    const latestTime = getTimestampMs(latest?.lastUpdated);

    if (!latest || variantTime > latestTime) {
      return variant;
    }

    return latest;
  }, null);

  if (!latestVariant) {
    return {
      latestPrice: '-',
      latestUpdate: '-',
      latestNote: '-',
      variantId: '',
      variantName: '',
      history: [],
    };
  }

  const latestEntry = getLatestPriceEntry(latestVariant.priceHistory);

  return {
    latestPrice: formatPrice(latestVariant.currentPrice ?? latestEntry?.price),
    latestUpdate: formatDate(latestVariant.lastUpdated ?? latestEntry?.date),
    latestNote: normalizeHistoryNote(latestVariant.notes || latestEntry?.note),
    variantId: latestVariant.id || '',
    variantName: getVariantName(latestVariant),
    history: latestVariant.priceHistory || [],
  };
};

const getComparablePricingValue = (row = {}) => {
  const numericValue = Number(row.priceValue ?? row.currentPrice);

  return Number.isFinite(numericValue) ? numericValue : null;
};

const getComparableUpdateTime = (row = {}) => {
  if (Number.isFinite(row.lastUpdatedTime)) {
    return row.lastUpdatedTime;
  }

  const parsedDate = parseDateValue(row.lastUpdated);

  return parsedDate ? parsedDate.getTime() : null;
};

const sortPricingRows = (
  rows,
  sortMode,
  getName = (row) => row?.supplierName || ''
) => {
  const direction = sortMode === 'highest' ? -1 : 1;

  return [...rows].sort((firstRow, secondRow) => {
    const firstName = getName(firstRow);
    const secondName = getName(secondRow);

    if (sortMode === 'supplier') {
      return firstName.localeCompare(secondName);
    }

    if (sortMode === 'latest') {
      const firstTime = getComparableUpdateTime(firstRow) ?? Number.NEGATIVE_INFINITY;
      const secondTime = getComparableUpdateTime(secondRow) ?? Number.NEGATIVE_INFINITY;

      if (firstTime !== secondTime) {
        return secondTime - firstTime;
      }
    } else {
      const firstPrice = getComparablePricingValue(firstRow);
      const secondPrice = getComparablePricingValue(secondRow);
      const firstHasPrice = firstPrice !== null;
      const secondHasPrice = secondPrice !== null;

      if (firstHasPrice !== secondHasPrice) {
        return firstHasPrice ? -1 : 1;
      }

      if (firstHasPrice && secondHasPrice && firstPrice !== secondPrice) {
        return (firstPrice - secondPrice) * direction;
      }
    }

    return firstName.localeCompare(secondName);
  });
};

const sortVariantPricingRows = (rows, sortMode) =>
  sortPricingRows(rows, sortMode);

const getTimestampMs = (value) => {
  const parsedDate = parseDateValue(value);

  return parsedDate ? parsedDate.getTime() : 0;
};

const normalizeSnapshotString = (value) =>
  typeof value === 'string' ? value.trim() : '';

const serializeSnapshotDate = (value) => {
  const parsedDate = parseDateValue(value);

  return parsedDate ? parsedDate.toISOString() : null;
};

const getSnapshotProductId = (bundle = {}) =>
  normalizeSnapshotString(
    bundle.productId ||
      bundle.configurationSnapshot?.productId ||
      bundle.configurationSnapshot?.product?.id
  );

const getSnapshotComponents = (snapshot = {}) =>
  Array.isArray(snapshot?.components) ? snapshot.components : [];

const normalizeComparableText = (value) =>
  String(value || '').trim().toLowerCase();

const DUPLICATE_COMPONENT_MESSAGE =
  'Component already exists in this configuration.';

const formatDimensionValue = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return '-';
  }

  return Number.isInteger(numericValue)
    ? String(numericValue)
    : String(Number(numericValue.toFixed(3)));
};

const getDimensionSummaryLabels = (dimensions = {}) => {
  const normalizedDimensions = normalizeConfigurationDimensions(dimensions);

  if (!normalizedDimensions.enabled) {
    return [];
  }

  return (normalizedDimensions.fields || [])
    .map((field) => `${getDimensionFieldLabel(field)} ${formatDimensionValue(normalizedDimensions[field])}`);
};

const getDimensionValueLabel = (dimensions = {}) => {
  const normalizedDimensions = normalizeConfigurationDimensions(dimensions);
  const values = (normalizedDimensions.fields || [])
    .map((field) => formatDimensionValue(normalizedDimensions[field]))
    .filter((value) => value !== '-');

  return values.length > 0 ? values.join(' x ') : '-';
};

const getCalculationSummaryLabels = (dimensions = {}) => {
  const normalizedDimensions = normalizeConfigurationDimensions(dimensions);
  const labels = [];

  if (normalizedDimensions.area !== null) {
    labels.push(`Area ${formatDimensionValue(normalizedDimensions.area)} m2`);
  }

  if (normalizedDimensions.volume !== null) {
    labels.push(`Volume ${formatDimensionValue(normalizedDimensions.volume)} m3`);
  }

  return labels;
};

const getDimensionInputValue = (dimensions = {}, fieldName) => {
  const value = dimensions?.[fieldName];

  return value === null || value === undefined ? '' : String(value);
};

const getComponentVariantIdentity = (component = {}) =>
  normalizeComparableText(
    component.sourceVariantName || component.sourceVariantId || ''
  );

const buildSourceProductSupplierComponentRow = (component = {}, productSupplier = {}) => {
  const latestPriceEntry = getLatestPriceEntry(productSupplier?.priceHistory);
  const price = Number(latestPriceEntry?.price);
  const priceHistory = sortPriceHistoryByDateDesc(
    getSafePriceHistory(productSupplier?.priceHistory)
  );

  return {
    id: productSupplier?.id || '',
    productSupplierId: productSupplier?.id || '',
    componentId: component?.id || '',
    supplierId: productSupplier?.supplierId || '',
    currentPrice: Number.isFinite(price) ? price : null,
    unit: latestPriceEntry?.unit || productSupplier?.unit || '',
    pricingMethod: normalizePricingMethod(
      latestPriceEntry?.pricingMethod || productSupplier?.pricingMethod
    ),
    lastUpdated: latestPriceEntry?.date || productSupplier?.lastUpdated || null,
    notes: normalizeHistoryNote(latestPriceEntry?.note || productSupplier?.notes),
    isActive: productSupplier?.isDeleted !== true,
    sourceType: 'productSupplier',
    priceHistory,
  };
};

const buildSourceProductVariantComponentRow = (
  component = {},
  variant = {},
  productSupplierMap = {}
) => {
  const variantName = getVariantName(variant);
  const productSupplier =
    productSupplierMap[variant?.productSupplierId] ||
    Object.values(productSupplierMap || {}).find(
      (supplierRow) => supplierRow?.supplierId === variant?.supplierId
    ) ||
    {};
  const supplierId = variant?.supplierId || productSupplier?.supplierId || '';
  const price = Number(variant?.currentPrice);
  const priceHistory = sortPriceHistoryByDateDesc(
    getSafePriceHistory(variant?.priceHistory)
  );

  return {
    id: variant?.id || '',
    productVariantId: variant?.id || '',
    productSupplierId: variant?.productSupplierId || productSupplier?.id || '',
    componentId: component?.id || '',
    supplierId,
    currentPrice: Number.isFinite(price) ? price : null,
    unit: productSupplier?.unit || '',
    pricingMethod: normalizePricingMethod(productSupplier?.pricingMethod),
    lastUpdated: variant?.lastUpdated || null,
    notes: normalizeHistoryNote(variant?.notes),
    isActive: variant?.isActive === true,
    sourceType: 'productVariant',
    sourceVariantId: variant?.id || component?.sourceVariantId || '',
    sourceVariantName: variantName || component?.sourceVariantName || '',
    priceHistory,
  };
};

const buildAllowedSupplierIdsByComponentId = (
  components = [],
  sourceProductSuppliersByComponentId = {},
  sourceProductVariantsByComponentId = {}
) =>
  (Array.isArray(components) ? components : []).reduce((lookup, component) => {
    if (!component?.id) {
      return lookup;
    }

    if (!component?.sourceProductId) {
      lookup[component.id] = null;
      return lookup;
    }

    if (component?.sourceVariantName) {
      const productSupplierMap = buildProductSupplierMap(
        sourceProductSuppliersByComponentId[component.id] || []
      );

      lookup[component.id] = (
        Array.isArray(sourceProductVariantsByComponentId[component.id])
          ? sourceProductVariantsByComponentId[component.id]
          : []
      )
        .filter((variant) => getVariantName(variant) === component.sourceVariantName)
        .map((variant) => getSupplierIdForVariant(variant, productSupplierMap))
        .filter(Boolean);

      return lookup;
    }

    lookup[component.id] = (
      Array.isArray(sourceProductSuppliersByComponentId[component.id])
        ? sourceProductSuppliersByComponentId[component.id]
        : []
    )
      .map((productSupplier) => productSupplier?.supplierId)
      .filter(Boolean);

    return lookup;
  }, {});

const getBundleUsageCount = (bundle, bundles = [], bundleComponents = []) => {
  const bundleId = normalizeSnapshotString(bundle?.id);

  if (!bundleId) {
    return 0;
  }

  const componentReferences = (Array.isArray(bundleComponents)
    ? bundleComponents
    : []
  ).filter(
    (bundleComponent) =>
      bundleComponent?.isDeleted !== true &&
      bundleComponent?.sourceType === 'bundle' &&
      bundleComponent?.sourceId === bundleId
  ).length;
  const duplicateReferences = (Array.isArray(bundles) ? bundles : []).filter(
    (relatedBundle) =>
      relatedBundle?.isDeleted !== true &&
      normalizeSnapshotString(relatedBundle?.duplicatedFromId) === bundleId
  ).length;

  return componentReferences + duplicateReferences;
};

const buildMixedProductConfigurationSnapshot = ({
  productId,
  product,
  dimensions = {},
  components = [],
  componentSuppliersByComponentId = {},
  expenses = [],
  getSupplierDisplayName,
}) => {
  const productCalculationMode = normalizeMixedProductCalculationMode(
    product?.calculationMode
  );
  const productRequiredInputs = normalizeMixedProductRequiredInputs(
    product?.requiredInputs
  );
  const configurationDimensions = normalizeConfigurationDimensions(dimensions, {
    calculationMode: productCalculationMode,
    requiredInputs: productRequiredInputs,
  });
  const pricingRows = calculateMixedProductComponents(
    components,
    componentSuppliersByComponentId,
    configurationDimensions
  );
  const expenseRows = (Array.isArray(expenses) ? expenses : []).map(
    (expense, index) => ({
      id: expense?.id || '',
      name: expense?.name || '',
      amount: Number.isFinite(Number(expense?.amount))
        ? Number(expense.amount)
        : null,
      sortOrder: Number.isFinite(Number(expense?.sortOrder))
        ? Number(expense.sortOrder)
        : index,
      notes: expense?.notes || '',
    })
  );
  const componentSubtotal = calculateMixedProductTotal(
    components,
    componentSuppliersByComponentId,
    configurationDimensions
  );
  const expensesSubtotal = calculateExpenseTotal(expenseRows);

  return {
    productId,
    dimensions: configurationDimensions,
    dimensionKey: getConfigurationDimensionKey(configurationDimensions),
    product: {
      id: productId,
      name: product?.name || '',
      description: product?.description || '',
      type: product?.type || 'mixed',
      categories: normalizeIdArray(product?.categories),
      requiredInputs: normalizeMixedProductRequiredInputs(product?.requiredInputs),
      calculationMode: productCalculationMode,
      updatedAt: serializeSnapshotDate(product?.updatedAt),
    },
    components: pricingRows.map((component) => {
      const componentSuppliers =
        componentSuppliersByComponentId?.[component.id] || [];

      return {
        id: component.id || '',
        productId: component.productId || productId,
        name: component.name || '',
        description: component.description || '',
        sourceProductId: component.sourceProductId || '',
        sourceProductName: component.sourceProductName || '',
        sourceVariantId: component.sourceVariantId || '',
        sourceVariantName: component.sourceVariantName || '',
        quantity: component.quantity ?? 1,
        unit: component.unit || '',
        sortOrder: component.sortOrder ?? 0,
        isRequired: component.isRequired === true,
        selectedComponentSupplierId: component.selectedComponentSupplierId || '',
        selectedSupplierId: component.selectedSupplierId || '',
        selectedSupplierName: component.selectedSupplierId
          ? getSupplierDisplayName(component.selectedSupplierId)
          : '',
        selectedUnitPrice: component.selectedUnitPrice ?? null,
        lineTotal: component.lineTotal ?? null,
        calculationBreakdown: component.calculationBreakdown || null,
        status: component.status || '',
        componentSuppliers: componentSuppliers.map((componentSupplier) => ({
          id: componentSupplier.id || '',
          componentId: componentSupplier.componentId || component.id || '',
          productSupplierId: componentSupplier.productSupplierId || '',
          productVariantId: componentSupplier.productVariantId || '',
          sourceVariantId: componentSupplier.sourceVariantId || '',
          sourceVariantName: componentSupplier.sourceVariantName || '',
          supplierId: componentSupplier.supplierId || '',
          supplierName: componentSupplier.supplierId
            ? getSupplierDisplayName(componentSupplier.supplierId)
            : '',
          currentPrice: componentSupplier.currentPrice ?? null,
          unit: componentSupplier.unit || '',
          pricingMethod: normalizePricingMethod(componentSupplier.pricingMethod),
          lastUpdated: serializeSnapshotDate(componentSupplier.lastUpdated),
          notes: componentSupplier.notes || '',
          isActive: componentSupplier.isActive === true,
        })),
      };
    }),
    expenses: expenseRows,
    totals: {
      componentCount: pricingRows.length,
      readyComponentCount: pricingRows.filter(
        (component) => component.status === 'ready'
      ).length,
      componentSubtotal,
      expensesSubtotal,
      grandTotal: componentSubtotal + expensesSubtotal,
    },
  };
};

const buildConfigurationRows = ({
  bundles = [],
  bundleComponents = [],
  bundleExtraCosts = [],
  productId = '',
  productComponents = [],
}) => {
  const productComponentIds = new Set(
    (Array.isArray(productComponents) ? productComponents : [])
      .map((component) => component?.id)
      .filter(Boolean)
  );
  const componentNamesById = (Array.isArray(productComponents)
    ? productComponents
    : []
  ).reduce((lookup, component) => {
    if (component?.id) {
      lookup[component.id] = component.name || component.id;
    }

    return lookup;
  }, {});
  const bundleComponentLookup = (Array.isArray(bundleComponents)
    ? bundleComponents
    : []
  ).reduce((lookup, bundleComponent) => {
    const bundleId = bundleComponent?.bundleId;

    if (!bundleId) {
      return lookup;
    }

    if (!lookup[bundleId]) {
      lookup[bundleId] = [];
    }

    lookup[bundleId].push(bundleComponent);
    return lookup;
  }, {});
  const bundleExpenseLookup = (Array.isArray(bundleExtraCosts)
    ? bundleExtraCosts
    : []
  ).reduce((lookup, expense) => {
    const bundleId = expense?.bundleId;

    if (!bundleId) {
      return lookup;
    }

    if (!lookup[bundleId]) {
      lookup[bundleId] = [];
    }

    lookup[bundleId].push(expense);
    return lookup;
  }, {});

  return (Array.isArray(bundles) ? bundles : [])
    .map((bundle) => {
      const snapshot = bundle.configurationSnapshot;
      const snapshotComponents = getSnapshotComponents(snapshot);
      const snapshotBelongsToProduct =
        Boolean(snapshot) && getSnapshotProductId(bundle) === productId;
      const relatedComponents = bundleComponentLookup[bundle.id] || [];
      const relatedExpenses = bundleExpenseLookup[bundle.id] || [];
      const relatedProductComponents = relatedComponents.filter((bundleComponent) => {
        const sourceType = bundleComponent?.sourceType || 'mixedProductComponent';

        return (
          (sourceType === 'mixedProductComponent' &&
            productComponentIds.has(bundleComponent?.sourceId)) ||
          (sourceType === 'product' && bundleComponent?.sourceId === productId)
        );
      });

      if (!snapshotBelongsToProduct && relatedProductComponents.length === 0) {
        return null;
      }

      return {
        id: bundle.id,
        productId: bundle.productId || getSnapshotProductId(bundle),
        name: bundle.name || 'Unnamed configuration',
        description: bundle.description || '',
        status: bundle.status || 'draft',
        notes: bundle.notes || '',
        isDeleted: bundle.isDeleted === true,
        deletedAt: bundle.deletedAt || null,
        isMain: bundle.isMain === true,
        createdBy: bundle.createdBy || '',
        savedAt: bundle.savedAt || null,
        snapshotVersion: bundle.snapshotVersion || null,
        configurationSnapshot: snapshot || null,
        duplicatedFromId: bundle.duplicatedFromId || '',
        createdAt: bundle.createdAt || null,
        updatedAt: bundle.updatedAt || bundle.savedAt || bundle.createdAt || null,
        lastSavedAt: bundle.savedAt || null,
        expenseCount: relatedExpenses.length,
        expensesTotal: calculateExpenseTotal(relatedExpenses),
        usageCount: getBundleUsageCount(bundle, bundles, bundleComponents),
        componentCount: snapshotBelongsToProduct
          ? snapshotComponents.length
          : relatedProductComponents.length,
        componentNames: snapshotBelongsToProduct
          ? snapshotComponents.map((component) => component.name).filter(Boolean)
          : relatedProductComponents
            .map((bundleComponent) => componentNamesById[bundleComponent.sourceId])
            .filter(Boolean),
        hasSnapshot: snapshotBelongsToProduct,
      };
    })
    .filter(Boolean)
    .sort((firstRow, secondRow) => {
      const firstTime = getTimestampMs(firstRow.updatedAt);
      const secondTime = getTimestampMs(secondRow.updatedAt);

      if (firstTime !== secondTime) {
        return secondTime - firstTime;
      }

      return firstRow.name.localeCompare(secondRow.name);
    });
};

export default function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [categoryMap, setCategoryMap] = useState({});
  const [supplierMap, setSupplierMap] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [productSuppliers, setProductSuppliers] = useState([]);
  const [productVariants, setProductVariants] = useState([]);
  const [productComponents, setProductComponents] = useState([]);
  const [activeProductComponents, setActiveProductComponents] = useState([]);
  const [componentProductOptions, setComponentProductOptions] = useState([]);
  const [productConfigurations, setProductConfigurations] = useState([]);
  const [configurationLibraryView, setConfigurationLibraryView] =
    useState('active');
  const [configurationLibrarySearchTerm, setConfigurationLibrarySearchTerm] =
    useState('');
  const [extraExpenses, setExtraExpenses] = useState([]);
  const [componentSuppliersByComponentId, setComponentSuppliersByComponentId] =
    useState({});
  const [
    allowedSupplierIdsByComponentId,
    setAllowedSupplierIdsByComponentId,
  ] = useState({});
  const [
    sourceProductSuppliersByComponentId,
    setSourceProductSuppliersByComponentId,
  ] = useState({});
  const [
    sourceProductVariantsByComponentId,
    setSourceProductVariantsByComponentId,
  ] = useState({});
  const [sourceProductVariantsByProductId, setSourceProductVariantsByProductId] =
    useState({});
  const [
    activeComponentSuppliersByComponentId,
    setActiveComponentSuppliersByComponentId,
  ] = useState({});
  const [componentSupplierLoadingByComponentId, setComponentSupplierLoadingByComponentId] =
    useState({});
  const [componentSelectionUpdatingByComponentId, setComponentSelectionUpdatingByComponentId] =
    useState({});
  const [expandedComponentIds, setExpandedComponentIds] = useState([]);
  const [componentFilter] = useState('active');
  const [selectedVariantName, setSelectedVariantName] = useState('');
  const [variantSort, setVariantSort] = useState('lowest');
  const [supplierPricingSort, setSupplierPricingSort] = useState('supplier');
  const [mixedSupplierSort, setMixedSupplierSort] = useState('lowest');
  const [mixedSupplierSearch, setMixedSupplierSearch] = useState('');
  const [addSupplierSearch, setAddSupplierSearch] = useState('');
  const [isAddSupplierOpen, setIsAddSupplierOpen] = useState(false);
  const [isManageSuppliersOpen, setIsManageSuppliersOpen] = useState(false);
  const [isSupplierPriceModalOpen, setIsSupplierPriceModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isComponentModalOpen, setIsComponentModalOpen] = useState(false);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [isComponentHistoryModalOpen, setIsComponentHistoryModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isConfigurationModalOpen, setIsConfigurationModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [componentModalMode, setComponentModalMode] = useState('add');
  const [configurationModalMode, setConfigurationModalMode] = useState('add');
  const [expenseModalMode, setExpenseModalMode] = useState('add');
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [selectedComponentHistory, setSelectedComponentHistory] = useState([]);
  const [selectedComponentHistoryTitle, setSelectedComponentHistoryTitle] =
    useState('');
  const [selectedHistory, setSelectedHistory] = useState([]);
  const [selectedSupplierName, setSelectedSupplierName] = useState('');
  const [supplierPendingRemoval, setSupplierPendingRemoval] = useState(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [price, setPrice] = useState('');
  const [pricingMethod, setPricingMethod] = useState(DEFAULT_PRICING_METHOD);
  const [notes, setNotes] = useState('');
  const [pageError, setPageError] = useState('');
  const [pageMessage, setPageMessage] = useState('');
  const [supplierPriceError, setSupplierPriceError] = useState('');
  const [editError, setEditError] = useState('');
  const [componentError, setComponentError] = useState('');
  const [expenseError, setExpenseError] = useState('');
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    type: 'simple',
    allowedVariants: [],
    variantDraft: '',
    requiredInputs: [],
    calculationMode: 'fixed',
    categories: [],
  });
  const [componentForm, setComponentForm] = useState(createEmptyComponentForm(id));
  const [componentProductSearch, setComponentProductSearch] = useState('');
  const [
    highlightedComponentProductIndex,
    setHighlightedComponentProductIndex,
  ] = useState(0);
  const [expenseForm, setExpenseForm] = useState(createEmptyExpenseForm());
  const [configurationForm, setConfigurationForm] = useState(
    createEmptyConfigurationForm()
  );
  const [configurationConflict, setConfigurationConflict] = useState(null);
  const [selectedConfigurationDraft, setSelectedConfigurationDraft] = useState(null);
  const [addSupplierError, setAddSupplierError] = useState('');
  const [isAddingSupplier, setIsAddingSupplier] = useState(false);
  const [isSubmittingSupplierPrice, setIsSubmittingSupplierPrice] = useState(false);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [isSubmittingComponent, setIsSubmittingComponent] = useState(false);
  const [isSubmittingExpense, setIsSubmittingExpense] = useState(false);
  const [isSubmittingConfiguration, setIsSubmittingConfiguration] = useState(false);
  const [isLoadingComponentHistory, setIsLoadingComponentHistory] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isUpdatingComponentStatus, setIsUpdatingComponentStatus] = useState(false);
  const [isUpdatingExpenseStatus, setIsUpdatingExpenseStatus] = useState(false);
  const [isRemovingSupplier, setIsRemovingSupplier] = useState(false);
  const [isSavingConfiguration, setIsSavingConfiguration] = useState(false);
  const [configurationAction, setConfigurationAction] = useState({
    type: '',
    id: '',
  });
  const [isConfigurationBuilderOpen, setIsConfigurationBuilderOpen] =
    useState(false);
  const [selectedConfigurationId, setSelectedConfigurationId] = useState('');
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);

  const refreshProductPricingData = async (productData = product) => {
    if (productData?.type === 'mixed') {
      setSupplierMap({});
      setProductSuppliers([]);
      setProductVariants([]);
      return;
    }

    const nextProductSuppliers = await getProductSuppliers(id);
    const safeProductSuppliers = Array.isArray(nextProductSuppliers)
      ? nextProductSuppliers
      : [];
    const variants =
      productData?.type === 'variant'
        ? await getActiveProductVariantsByProductId(id)
        : [];
    const safeVariants = Array.isArray(variants) ? variants : [];
    const productSupplierMap = buildProductSupplierMap(safeProductSuppliers);
    const linkedSupplierIds = getLinkedSupplierIds(
      safeProductSuppliers,
      safeVariants,
      productSupplierMap
    );
    const linkedSuppliers = await getSuppliersByIds(linkedSupplierIds);
    const nextSupplierMap = buildPricingSuppliersById(linkedSuppliers);

    safeProductSuppliers.forEach((productSupplier) => {
      if (!String(productSupplier?.supplierId || '').trim()) {
        console.warn(
          '[ProductDetails] Supplier lookup failed because the product-supplier relation has no supplierId.',
          { productId: id, productSupplierId: productSupplier?.id || null }
        );
      }
    });
    linkedSupplierIds.forEach((supplierId) => {
      if (!nextSupplierMap[supplierId]) {
        console.warn(
          '[ProductDetails] Supplier lookup failed because the linked supplier document does not exist.',
          { productId: id, supplierId }
        );
      }
    });

    setSupplierMap(nextSupplierMap);
    setProductSuppliers(safeProductSuppliers);
    setProductVariants(safeVariants);
  };

  const loadComponentSuppliersByComponentId = useCallback(async (components = []) => {
    const componentEntries = await Promise.all(
      (Array.isArray(components) ? components : []).map(async (component) => {
        if (!component?.id) {
          return null;
        }

        const componentSuppliers = await getComponentSuppliers(component.id);

        return [
          component.id,
          Array.isArray(componentSuppliers) ? componentSuppliers : [],
        ];
      })
    );

    return componentEntries.reduce((accumulator, entry) => {
      if (entry) {
        const [componentId, componentSuppliers] = entry;

        accumulator[componentId] = componentSuppliers;
      }

      return accumulator;
    }, {});
  }, []);

  const loadSourceProductSuppliersByComponentId = useCallback(async (components = []) => {
    const componentEntries = await Promise.all(
      (Array.isArray(components) ? components : []).map(async (component) => {
        if (!component?.id || !component?.sourceProductId) {
          return component?.id ? [component.id, null] : null;
        }

        const sourceProductSuppliers = await getProductSuppliers(
          component.sourceProductId
        );
        return [
          component.id,
          Array.isArray(sourceProductSuppliers) ? sourceProductSuppliers : [],
        ];
      })
    );

    return componentEntries.reduce((accumulator, entry) => {
      if (entry) {
        const [componentId, sourceProductSuppliers] = entry;

        accumulator[componentId] = sourceProductSuppliers;
      }

      return accumulator;
    }, {});
  }, []);

  const loadSourceProductVariantsByComponentId = useCallback(async (components = []) => {
    const componentEntries = await Promise.all(
      (Array.isArray(components) ? components : []).map(async (component) => {
        if (!component?.id || !component?.sourceProductId) {
          return component?.id ? [component.id, []] : null;
        }

        const sourceProductVariants = await getActiveProductVariantsByProductId(
          component.sourceProductId
        );

        return [
          component.id,
          Array.isArray(sourceProductVariants) ? sourceProductVariants : [],
        ];
      })
    );

    return componentEntries.reduce((accumulator, entry) => {
      if (entry) {
        const [componentId, sourceProductVariants] = entry;

        accumulator[componentId] = sourceProductVariants;
      }

      return accumulator;
    }, {});
  }, []);

  const refreshProductConfigurations = useCallback(async (components = []) => {
    try {
      const [bundles, bundleComponents, bundleExtraCosts] = await Promise.all([
        getBundles({ includeDeleted: true }),
        getBundleComponents(),
        getBundleExtraCosts(),
      ]);

      setProductConfigurations(buildConfigurationRows({
        bundles,
        bundleComponents,
        bundleExtraCosts,
        productId: id,
        productComponents: components,
      }));
    } catch (error) {
      console.error('Error fetching product bundles:', error);
      setProductConfigurations([]);
    }
  }, [id]);

  const loadExpenseRowsForBundle = useCallback(async (bundleId) => {
    const normalizedBundleId = String(bundleId || '').trim();

    if (!normalizedBundleId) {
      return [];
    }

    const records = await getBundleExtraCosts();

    return (Array.isArray(records) ? records : [])
      .filter((expense) => expense.bundleId === normalizedBundleId)
      .sort((firstExpense, secondExpense) => {
        if (firstExpense.sortOrder !== secondExpense.sortOrder) {
          return firstExpense.sortOrder - secondExpense.sortOrder;
        }

        return (firstExpense.name || '').localeCompare(secondExpense.name || '');
      });
  }, []);

  const refreshExtraExpenses = useCallback(async () => {
    const workingExpenses = await loadExpenseRowsForBundle(
      getWorkingExpenseBundleId(id)
    );

    setExtraExpenses(workingExpenses);
  }, [id, loadExpenseRowsForBundle]);

  const refreshProductComponents = useCallback(async (filter = componentFilter) => {
    const includeDeleted = filter === 'deleted';
    const components = await getProductComponents(id, { includeDeleted });
    const safeComponents = Array.isArray(components) ? components : [];
    const componentSuppliers = await loadComponentSuppliersByComponentId(
      safeComponents
    );
    const sourceProductSuppliers = await loadSourceProductSuppliersByComponentId(
      safeComponents
    );
    const sourceProductVariants = await loadSourceProductVariantsByComponentId(
      safeComponents
    );
    const allowedSupplierIds = buildAllowedSupplierIdsByComponentId(
      safeComponents,
      sourceProductSuppliers,
      sourceProductVariants
    );

    setProductComponents(safeComponents);
    setComponentSuppliersByComponentId(componentSuppliers);
    setSourceProductSuppliersByComponentId(sourceProductSuppliers);
    setSourceProductVariantsByComponentId(sourceProductVariants);
    setAllowedSupplierIdsByComponentId(allowedSupplierIds);

    if (includeDeleted) {
      const activeComponents = await getProductComponents(id, {
        includeDeleted: false,
      });
      const safeActiveComponents = Array.isArray(activeComponents)
        ? activeComponents
        : [];
      const activeComponentSuppliers = await loadComponentSuppliersByComponentId(
        safeActiveComponents
      );
      const activeSourceProductSuppliers =
        await loadSourceProductSuppliersByComponentId(
        safeActiveComponents
      );
      const activeSourceProductVariants =
        await loadSourceProductVariantsByComponentId(
          safeActiveComponents
        );
      const activeAllowedSupplierIds = buildAllowedSupplierIdsByComponentId(
        safeActiveComponents,
        activeSourceProductSuppliers,
        activeSourceProductVariants
      );

      setActiveProductComponents(safeActiveComponents);
      setActiveComponentSuppliersByComponentId(activeComponentSuppliers);
      setSourceProductSuppliersByComponentId(activeSourceProductSuppliers);
      setSourceProductVariantsByComponentId(activeSourceProductVariants);
      setAllowedSupplierIdsByComponentId(activeAllowedSupplierIds);
      await refreshProductConfigurations(safeActiveComponents);
    } else {
      setActiveProductComponents(safeComponents);
      setActiveComponentSuppliersByComponentId(componentSuppliers);
      await refreshProductConfigurations(safeComponents);
    }
  }, [
    componentFilter,
    id,
    loadSourceProductSuppliersByComponentId,
    loadSourceProductVariantsByComponentId,
    loadComponentSuppliersByComponentId,
    refreshProductConfigurations,
  ]);

  const refreshComponentSupplierPricing = useCallback(async (componentId) => {
    if (!componentId) {
      return;
    }

    setComponentSupplierLoadingByComponentId((prev) => ({
      ...prev,
      [componentId]: true,
    }));

    try {
      const componentSuppliers = await getComponentSuppliers(componentId);

      setComponentSuppliersByComponentId((prev) => ({
        ...prev,
        [componentId]: Array.isArray(componentSuppliers) ? componentSuppliers : [],
      }));
    } finally {
      setComponentSupplierLoadingByComponentId((prev) => ({
        ...prev,
        [componentId]: false,
      }));
    }
  }, []);

  const resetSupplierPriceForm = () => {
    setSelectedSupplierId('');
    setPrice('');
    setPricingMethod(DEFAULT_PRICING_METHOD);
    setNotes('');
    setSupplierPriceError('');
    setIsSubmittingSupplierPrice(false);
  };

  const resetAddSupplierForm = () => {
    setSelectedSupplierId('');
    setAddSupplierSearch('');
    setAddSupplierError('');
    setIsAddingSupplier(false);
  };

  const getActiveVariantName = () => {
    const variantNames = getSelectableVariantNames(product, productVariants);

    return selectedVariantName && variantNames.includes(selectedVariantName)
      ? selectedVariantName
      : variantNames[0] || '';
  };

  const buildEditFormFromProduct = (productData) => ({
    name: productData?.name || '',
    description: productData?.description || '',
    type: normalizeEditableProductType(productData?.type),
    allowedVariants: normalizeVariantNames(productData?.allowedVariants),
    variantDraft: '',
    requiredInputs: normalizeMixedProductRequiredInputs(productData?.requiredInputs),
    calculationMode: normalizeMixedProductCalculationMode(
      productData?.calculationMode
    ),
    categories: normalizeIdArray(productData?.categories),
  });

  const resetEditForm = () => {
    setEditForm(buildEditFormFromProduct(product));
    setEditError('');
    setIsSubmittingEdit(false);
  };

  const handleOpenAddSupplierModal = () => {
    resetAddSupplierForm();
    setIsAddSupplierOpen(true);
  };

  const handleCloseAddSupplierModal = () => {
    if (isAddingSupplier) {
      return;
    }

    setIsAddSupplierOpen(false);
    resetAddSupplierForm();
  };

  const handleCloseManageSuppliersModal = () => {
    setIsManageSuppliersOpen(false);
  };

  const handleCloseSupplierPriceModal = () => {
    if (isSubmittingSupplierPrice) {
      return;
    }

    setIsSupplierPriceModalOpen(false);
    resetSupplierPriceForm();
  };

  const handleOpenEditModal = () => {
    setEditForm(buildEditFormFromProduct(product));
    setEditError('');
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    if (isSubmittingEdit) {
      return;
    }

    setIsEditModalOpen(false);
    resetEditForm();
  };

  const handleOpenHistoryModal = (supplierName, priceHistory) => {
    setSelectedSupplierName(supplierName || '');
    setSelectedHistory(sortPriceHistoryByDateDesc(priceHistory));
    setIsLoadingHistory(false);
    setIsHistoryModalOpen(true);
  };

  const handleOpenVariantHistoryModal = async (row) => {
    const selectedActiveVariantName = getActiveVariantName();

    setSelectedSupplierName(
      `${row?.supplierName || 'Supplier'} / ${row?.variantName || selectedActiveVariantName || 'Variant'}`
    );
    setSelectedHistory([]);
    setIsLoadingHistory(true);
    setIsHistoryModalOpen(true);

    if (!row?.variantId) {
      setSelectedHistory([]);
      setIsLoadingHistory(false);
      return;
    }

    try {
      const history = await getVariantPriceHistory(row?.variantId, []);
      setSelectedHistory(Array.isArray(history) ? history : []);
    } catch (error) {
      console.error('Error loading variant price history:', error);
      setSelectedHistory([]);
      setPageError('Failed to load price history.');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleCloseHistoryModal = () => {
    setIsHistoryModalOpen(false);
    setSelectedHistory([]);
    setSelectedSupplierName('');
    setIsLoadingHistory(false);
  };

  const handleOpenRemoveModal = (row, targetType = 'supplier') => {
    setSupplierPendingRemoval({
      ...row,
      removalType: targetType,
    });
    setIsRemoveModalOpen(true);
  };

  const handleCloseRemoveModal = () => {
    if (isRemovingSupplier) {
      return;
    }

    setIsRemoveModalOpen(false);
    setSupplierPendingRemoval(null);
  };

  useEffect(() => {
    const fetchProductDetails = async () => {
      setLoading(true);
      setPageError('');

      try {
        const [nextProduct, categories] =
          await Promise.all([
            getProductById(id),
            getCategories(),
          ]);

        setCategoryMap(buildLookupMap(categories, 'name'));

        if (!nextProduct) {
          setProduct(null);
          return;
        }

        setProduct(nextProduct);

        if (nextProduct?.type === 'mixed') {
          try {
            const [components, mixedSuppliers, products, workingExpenses] = await Promise.all([
              getProductComponents(id, { includeDeleted: false }),
              getSuppliers(),
              getProducts(),
              loadExpenseRowsForBundle(getWorkingExpenseBundleId(id)),
            ]);
            const safeComponents = Array.isArray(components) ? components : [];
            const mixedComponentSuppliers =
              await loadComponentSuppliersByComponentId(safeComponents);
            const mixedSourceProductSuppliers =
              await loadSourceProductSuppliersByComponentId(safeComponents);
            const mixedSourceProductVariants =
              await loadSourceProductVariantsByComponentId(safeComponents);
            const mixedAllowedSupplierIds = buildAllowedSupplierIdsByComponentId(
              safeComponents,
              mixedSourceProductSuppliers,
              mixedSourceProductVariants
            );
            const availableComponentProducts = (Array.isArray(products) ? products : [])
              .filter((item) => item?.id && item.id !== id)
              .filter((item) => item.isDeleted !== true)
              .sort((firstProduct, secondProduct) =>
                (firstProduct.name || '').localeCompare(secondProduct.name || '')
              );

            setSuppliers(Array.isArray(mixedSuppliers) ? mixedSuppliers : []);
            setComponentProductOptions(availableComponentProducts);
            setExtraExpenses(Array.isArray(workingExpenses) ? workingExpenses : []);
            setSupplierMap({});
            setProductSuppliers([]);
            setProductVariants([]);
            setProductComponents(safeComponents);
            setActiveProductComponents(safeComponents);
            await refreshProductConfigurations(safeComponents);
            setComponentSuppliersByComponentId(mixedComponentSuppliers);
            setActiveComponentSuppliersByComponentId(mixedComponentSuppliers);
            setSourceProductSuppliersByComponentId(mixedSourceProductSuppliers);
            setSourceProductVariantsByComponentId(mixedSourceProductVariants);
            setAllowedSupplierIdsByComponentId(mixedAllowedSupplierIds);
            setExpandedComponentIds([]);
          } catch (error) {
            console.error('Error fetching mixed product relations:', error);
            setSuppliers([]);
            setComponentProductOptions([]);
            setExtraExpenses([]);
            setSupplierMap({});
            setProductSuppliers([]);
            setProductVariants([]);
            setProductComponents([]);
            setActiveProductComponents([]);
            setProductConfigurations([]);
            setComponentSuppliersByComponentId({});
            setActiveComponentSuppliersByComponentId({});
            setSourceProductSuppliersByComponentId({});
            setSourceProductVariantsByComponentId({});
            setSourceProductVariantsByProductId({});
            setAllowedSupplierIdsByComponentId({});
            setExpandedComponentIds([]);
            setPageError('Failed to load product components.');
          }
          return;
        }

        try {
          const [suppliers, nextProductSuppliers] = await Promise.all([
            getSuppliers(),
            getProductSuppliers(id),
          ]);
          const allSuppliers = Array.isArray(suppliers) ? suppliers : [];
          const safeProductSuppliers = Array.isArray(nextProductSuppliers)
            ? nextProductSuppliers
            : [];
          const variants =
            nextProduct?.type === 'variant'
              ? await getActiveProductVariantsByProductId(id)
              : [];
          const safeVariants = Array.isArray(variants) ? variants : [];
          const productSupplierMap = buildProductSupplierMap(safeProductSuppliers);
          const linkedSupplierIds = getLinkedSupplierIds(
            safeProductSuppliers,
            safeVariants,
            productSupplierMap
          );
          const linkedSuppliers = await getSuppliersByIds(linkedSupplierIds);
          const nextSupplierMap = buildPricingSuppliersById(linkedSuppliers);

          safeProductSuppliers.forEach((productSupplier) => {
            if (!String(productSupplier?.supplierId || '').trim()) {
              console.warn(
                '[ProductDetails] Supplier lookup failed because the product-supplier relation has no supplierId.',
                { productId: id, productSupplierId: productSupplier?.id || null }
              );
            }
          });
          linkedSupplierIds.forEach((supplierId) => {
            if (!nextSupplierMap[supplierId]) {
              console.warn(
                '[ProductDetails] Supplier lookup failed because the linked supplier document does not exist.',
                { productId: id, supplierId }
              );
            }
          });

          setSuppliers(allSuppliers);
          setSupplierMap(nextSupplierMap);
          setProductSuppliers(safeProductSuppliers);
          setProductVariants(safeVariants);
        } catch (error) {
          console.error('Error fetching product pricing relations:', error);
          setSuppliers([]);
          setSupplierMap({});
          setProductSuppliers([]);
          setProductVariants([]);
          setPageError('Failed to load product pricing data.');
        }

        setProductComponents([]);
        setActiveProductComponents([]);
        setComponentProductOptions([]);
        setExtraExpenses([]);
        setProductConfigurations([]);
        setComponentSuppliersByComponentId({});
        setActiveComponentSuppliersByComponentId({});
        setSourceProductSuppliersByComponentId({});
        setSourceProductVariantsByComponentId({});
        setSourceProductVariantsByProductId({});
        setAllowedSupplierIdsByComponentId({});
        setExpandedComponentIds([]);
      } catch (error) {
        console.error('Error fetching product details:', error);
        setProduct(null);
        setCategoryMap({});
        setSupplierMap({});
        setSuppliers([]);
        setProductSuppliers([]);
        setProductVariants([]);
        setProductComponents([]);
        setActiveProductComponents([]);
        setComponentProductOptions([]);
        setExtraExpenses([]);
        setProductConfigurations([]);
        setComponentSuppliersByComponentId({});
        setActiveComponentSuppliersByComponentId({});
        setSourceProductSuppliersByComponentId({});
        setSourceProductVariantsByComponentId({});
        setSourceProductVariantsByProductId({});
        setAllowedSupplierIdsByComponentId({});
        setExpandedComponentIds([]);
        setPageError('Failed to load product details.');
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [
    id,
    loadComponentSuppliersByComponentId,
    loadSourceProductSuppliersByComponentId,
    loadSourceProductVariantsByComponentId,
    loadExpenseRowsForBundle,
    refreshProductConfigurations,
  ]);

  useEffect(() => {
    if (product?.type !== 'variant') {
      setSelectedVariantName('');
      return;
    }

    const variantNames = getSelectableVariantNames(product, productVariants);

    setSelectedVariantName((currentName) =>
      currentName && variantNames.includes(currentName)
        ? currentName
        : variantNames[0] || ''
    );
  }, [product, productVariants]);

  useEffect(() => {
    if (product?.type !== 'mixed') {
      return;
    }

    setExpandedComponentIds([]);
    setComponentSuppliersByComponentId({});
    setSourceProductSuppliersByComponentId({});
    setSourceProductVariantsByComponentId({});
    setSourceProductVariantsByProductId({});
    setAllowedSupplierIdsByComponentId({});
    refreshProductComponents(componentFilter).catch((error) => {
      console.error('Error fetching product components:', error);
      setPageError('Failed to load product components.');
    });
  }, [componentFilter, product?.type, refreshProductComponents]);

  useEffect(() => {
    setHighlightedComponentProductIndex(0);
  }, [componentProductSearch, componentProductOptions]);

  if (loading) {
    return <p>Loading product...</p>;
  }

  if (!product) {
    return <p>Product not found</p>;
  }

  const productName = product.name || '-';
  const isVariantProduct = product.type === 'variant';
  const isMixedProduct = product.type === 'mixed';
  const categoryNames = getDisplayValues(product.categories, categoryMap);
  const allowedVariantNames = normalizeVariantNames(product.allowedVariants);
  const requiredInputLabels = getRequiredInputLabels(product.requiredInputs);
  const productCalculationMode = normalizeMixedProductCalculationMode(
    product.calculationMode
  );
  const productDimensionFields = getConfigurationDimensionFields({
    requiredInputs: product.requiredInputs,
    calculationMode: productCalculationMode,
  });
  const variantNames = getSelectableVariantNames(product, productVariants);
  const activeVariantName = getActiveVariantName();
  const availableSuppliers = (Array.isArray(suppliers) ? suppliers : []).filter(
    (supplier) => supplier?.id && (supplier.companyName || '').trim() && supplier.isDeleted !== true
  );
  const isSupplierPickerLocked = Boolean(selectedSupplierId);
  const productSupplierMap = buildProductSupplierMap(productSuppliers);
  const linkedSupplierIds = new Set(
    getLinkedSupplierIds(
      productSuppliers,
      productVariants,
      productSupplierMap
    )
  );
  const availableSuppliersToLink = availableSuppliers.filter(
    (supplier) => !linkedSupplierIds.has(supplier.id)
  );
  const filteredSuppliersToLink = availableSuppliersToLink.filter((supplier) => {
    const normalizedSearchTerm = addSupplierSearch.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return true;
    }

    return String(supplier.companyName || supplier.name || '')
      .toLowerCase()
      .includes(normalizedSearchTerm);
  });
  const supplierPricingRows = isVariantProduct || isMixedProduct
    ? []
    : sortPricingRows(
      productSuppliers
        .map((productSupplier) => buildSupplierPricingRow(productSupplier, supplierMap))
        .filter(Boolean),
      supplierPricingSort
    );
  const variantPricingRows =
    isVariantProduct && variantNames.length > 0
      ? sortVariantPricingRows(
        (Array.isArray(productVariants) ? productVariants : [])
          .filter((variant) => getVariantName(variant) === activeVariantName)
          .map((variant) =>
            buildVariantPricingRow(
              variant,
              supplierMap,
              productSupplierMap
            )
          )
          .filter((row) => row?.hasVariant),
        variantSort
      )
      : [];
  const linkedSupplierManagementRows = isVariantProduct
    ? sortPricingRows(
      productSuppliers
        .map((productSupplier) => {
          const supplierId = productSupplier?.supplierId || '';
          const supplierLookupRow = buildSupplierLookupRow(
            { supplierId },
            supplierMap
          );

          if (!supplierId || !supplierLookupRow) {
            return null;
          }

          const latestVariantPricing = getLatestVariantPricingForSupplier(
            supplierId,
            productVariants,
            productSupplierMap
          );
          const pricingMethod = normalizePricingMethod(
            productSupplier?.pricingMethod
          );

          return {
            id: productSupplier?.id || `${supplierId}-supplier-link`,
            supplierId,
            supplierName: supplierLookupRow.supplierName,
            isTrashed: supplierLookupRow.isTrashed,
            isMissingSupplier: supplierLookupRow.isMissingSupplier === true,
            pricingMethod,
            pricingMethodLabel: getPricingMethodLabel(pricingMethod),
            price:
              latestVariantPricing.latestPrice === '-'
                ? '-'
                : formatPriceWithPricingMethod(
                  latestVariantPricing.latestPrice,
                  pricingMethod
                ),
            date: latestVariantPricing.latestUpdate,
            note: latestVariantPricing.latestNote,
            variantId: latestVariantPricing.variantId,
            variantName: latestVariantPricing.variantName,
            history: latestVariantPricing.history,
          };
        })
        .filter(Boolean),
      supplierPricingSort
    )
    : supplierPricingRows;
  function getSupplierDisplayName(supplierId) {
    const supplier = (Array.isArray(suppliers) ? suppliers : []).find(
      (item) => item.id === supplierId
    );

    if (!supplier) {
      return 'Deleted Supplier';
    }

    return `${supplier.companyName || supplier.name || 'Archived Supplier'}${
      supplier.isDeleted ? ' (Archived Supplier)' : ''
    }`;
  }

  function getSupplierRecord(supplierId) {
    return (Array.isArray(suppliers) ? suppliers : []).find(
      (item) => item.id === supplierId
    ) || null;
  }

  function isSupplierDeletedOrMissing(supplierId) {
    const supplier = getSupplierRecord(supplierId);

    return !supplier || supplier.isDeleted === true;
  }

  function isComponentSupplierPreviouslySelected(component, componentSupplier) {
    if (!componentSupplier?.id) {
      return false;
    }

    return (
      component.selectedComponentSupplierId === componentSupplier.id ||
      (
        !component.selectedComponentSupplierId &&
        component.selectedSupplierId &&
        component.selectedSupplierId === componentSupplier.supplierId
      )
    );
  }

  function getComponentSupplierDisplayName(component, componentSupplier) {
    if (!componentSupplier?.supplierId) {
      return '-';
    }

    if (isSupplierDeletedOrMissing(componentSupplier.supplierId)) {
      return isComponentSupplierPreviouslySelected(component, componentSupplier)
        ? '⚠ Deleted Supplier (Previously Selected)'
        : '';
    }

    const supplier = getSupplierRecord(componentSupplier.supplierId);

    return supplier?.companyName || supplier?.name || 'Unnamed supplier';
  }

  function getVisibleComponentSupplierRows(component, rows = []) {
    return (Array.isArray(rows) ? rows : []).filter((componentSupplier) =>
      !isSupplierDeletedOrMissing(componentSupplier?.supplierId) ||
      isComponentSupplierPreviouslySelected(component, componentSupplier)
    );
  }

  function getComponentAllowedSupplierIds(component) {
    if (!component?.sourceProductId) {
      return null;
    }

    return Array.isArray(allowedSupplierIdsByComponentId[component.id])
      ? allowedSupplierIdsByComponentId[component.id]
      : [];
  }

  function getSourceProductSuppliersForComponent(component) {
    if (!component?.id || !component?.sourceProductId) {
      return null;
    }

    return Array.isArray(sourceProductSuppliersByComponentId[component.id])
      ? sourceProductSuppliersByComponentId[component.id]
      : [];
  }

  function getSourceProductVariantsForComponent(component) {
    if (!component?.id || !component?.sourceProductId) {
      return [];
    }

    return Array.isArray(sourceProductVariantsByComponentId[component.id])
      ? sourceProductVariantsByComponentId[component.id]
      : [];
  }

  function getSourceProductSupplierComponentRows(component) {
    if (component?.sourceVariantName) {
      const sourceProductVariants = getSourceProductVariantsForComponent(component);
      const sourceProductSupplierMap = buildProductSupplierMap(
        getSourceProductSuppliersForComponent(component) || []
      );

      return sourceProductVariants
        .filter((variant) =>
          getVariantName(variant) === component.sourceVariantName
        )
        .map((variant) =>
          buildSourceProductVariantComponentRow(
            component,
            variant,
            sourceProductSupplierMap
          )
        )
        .filter((componentSupplier) => componentSupplier.id)
        .filter((componentSupplier) => componentSupplier.supplierId);
    }

    const sourceProductSuppliers = getSourceProductSuppliersForComponent(component);

    if (!Array.isArray(sourceProductSuppliers)) {
      return null;
    }

    return sourceProductSuppliers
      .map((productSupplier) =>
        buildSourceProductSupplierComponentRow(component, productSupplier)
      )
      .filter((componentSupplier) => componentSupplier.id)
      .filter((componentSupplier) => componentSupplier.supplierId);
  }

  function getFilteredComponentSupplierRows(component, rows = []) {
    const allowedSupplierIds = getComponentAllowedSupplierIds(component);

    if (!Array.isArray(allowedSupplierIds)) {
      return Array.isArray(rows) ? rows : [];
    }

    const allowedSupplierIdSet = new Set(allowedSupplierIds);

    return (Array.isArray(rows) ? rows : []).filter((componentSupplier) =>
      allowedSupplierIdSet.has(componentSupplier?.supplierId)
    );
  }

  function getConfigurationComponentSupplierRows(component, rows = []) {
    const sourceProductSupplierRows =
      getSourceProductSupplierComponentRows(component);

    if (
      Array.isArray(sourceProductSupplierRows) &&
      sourceProductSupplierRows.length > 0
    ) {
      return getVisibleComponentSupplierRows(component, sourceProductSupplierRows);
    }

    return getVisibleComponentSupplierRows(
      component,
      getFilteredComponentSupplierRows(component, rows)
    );
  }

  const filteredComponentSuppliersByComponentId = isMixedProduct
    ? activeProductComponents.reduce((lookup, component) => {
      lookup[component.id] = getConfigurationComponentSupplierRows(
        component,
        activeComponentSuppliersByComponentId[component.id] || []
      );
      return lookup;
    }, {})
    : {};
  const activeBuilderDimensions = normalizeConfigurationDimensions(
    selectedConfigurationDraft?.dimensions,
    {
      calculationMode:
        selectedConfigurationDraft?.dimensions?.calculationMode ||
        productCalculationMode,
      requiredInputs:
        selectedConfigurationDraft?.dimensions?.fields ||
        product.requiredInputs,
    }
  );

  const mixedComponentPricingRows = isMixedProduct
    ? calculateMixedProductComponents(
      activeProductComponents,
      filteredComponentSuppliersByComponentId,
      activeBuilderDimensions
    )
    : [];
  const mixedComponentSubtotal = isMixedProduct
    ? calculateMixedProductTotal(
      activeProductComponents,
      filteredComponentSuppliersByComponentId,
      activeBuilderDimensions
    )
    : 0;
  const extraExpensesTotal = isMixedProduct
    ? calculateExpenseTotal(extraExpenses)
    : 0;
  const mixedGrandTotal = mixedComponentSubtotal + extraExpensesTotal;
  const normalizedComponentProductSearch = componentProductSearch
    .trim()
    .toLowerCase();
  const filteredComponentProductOptions = componentProductOptions.filter((item) => {
    if (!normalizedComponentProductSearch) {
      return true;
    }

    return [item.name, item.description]
      .filter(Boolean)
      .some((value) =>
        String(value).toLowerCase().includes(normalizedComponentProductSearch)
      );
  });
  const selectedComponentSourceProduct = componentProductOptions.find(
    (item) => item.id === componentForm.sourceProductId
  ) || null;
  const selectedSourceProductVariants =
    sourceProductVariantsByProductId[componentForm.sourceProductId] || [];
  const sourceVariantOptions = getVariantSelectionOptions(
    selectedComponentSourceProduct,
    selectedSourceProductVariants
  );
  const isSelectedSourceVariantProduct =
    selectedComponentSourceProduct?.type === 'variant' ||
    Boolean(componentForm.sourceVariantName);
  const isSourceVariantSelectionMissing =
    isSelectedSourceVariantProduct && !componentForm.sourceVariantName;
  const isConfigurationActionRunning =
    isSavingConfiguration || Boolean(configurationAction.type);
  const configurationLibraryRows = productConfigurations
    .map((configuration) => {
      const snapshotProduct =
        configuration.configurationSnapshot?.product &&
        typeof configuration.configurationSnapshot.product === 'object'
          ? configuration.configurationSnapshot.product
          : {};
      const snapshotCategoryIds = normalizeIdArray(snapshotProduct.categories);
      const categoryIds =
        snapshotCategoryIds.length > 0
          ? snapshotCategoryIds
          : normalizeIdArray(product.categories);
      const categoryLabels = categoryIds
        .map((categoryId) => categoryMap[categoryId] || categoryId)
        .filter(Boolean);
      const dimensions = normalizeConfigurationDimensions(
        configuration.dimensions ||
        configuration.configurationSnapshot?.dimensions,
        {
          calculationMode:
            configuration.dimensions?.calculationMode ||
            configuration.configurationSnapshot?.dimensions?.calculationMode ||
            configuration.configurationSnapshot?.product?.calculationMode ||
            productCalculationMode,
          requiredInputs:
            configuration.dimensions?.fields ||
            configuration.configurationSnapshot?.dimensions?.fields ||
            configuration.configurationSnapshot?.product?.requiredInputs ||
            product.requiredInputs,
        }
      );
      const dimensionLabels = getDimensionSummaryLabels(dimensions);
      const calculationLabels = getCalculationSummaryLabels(dimensions);
      const dimensionDisplay = getDimensionValueLabel(dimensions);

      return {
        ...configuration,
        categoryIds,
        categoryLabels,
        dimensions,
        dimensionKey: getConfigurationDimensionKey(dimensions),
        dimensionLabels,
        calculationLabels,
        dimensionDisplay,
        recommended: configuration.isMain === true,
        searchText: [
          configuration.id,
          configuration.name,
          configuration.description,
          configuration.status,
          ...categoryLabels,
          ...dimensionLabels,
          ...calculationLabels,
          dimensionDisplay,
          getConfigurationDimensionKey(dimensions),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase(),
      };
    })
    .filter((configuration) =>
      configurationLibraryView === 'trash'
        ? configuration.isDeleted === true
        : configuration.isDeleted !== true
    )
    .filter((configuration) => {
      const normalizedSearchTerm = configurationLibrarySearchTerm
        .trim()
        .toLowerCase();

      if (!normalizedSearchTerm) {
        return true;
      }

      return configuration.searchText.includes(normalizedSearchTerm);
    })
    .sort((firstConfiguration, secondConfiguration) => {
      if (firstConfiguration.recommended !== secondConfiguration.recommended) {
        return firstConfiguration.recommended ? -1 : 1;
      }

      return firstConfiguration.name.localeCompare(secondConfiguration.name);
    });
  const shouldShowConfigurationBuilder =
    isMixedProduct &&
    isConfigurationBuilderOpen;
  const selectedConfiguration = selectedConfigurationId
    ? configurationLibraryRows.find(
      (configuration) => configuration.id === selectedConfigurationId
    )
    : null;
  const builderConfigurationMeta = selectedConfigurationDraft ||
    (selectedConfiguration
      ? {
        id: selectedConfiguration.id,
        name: selectedConfiguration.name,
        categoryIds: selectedConfiguration.categoryIds,
        dimensions: selectedConfiguration.dimensions,
        recommended: selectedConfiguration.recommended,
      }
      : null);
  const builderCategoryLabels = builderConfigurationMeta?.categoryIds?.length
    ? builderConfigurationMeta.categoryIds
      .map((categoryId) => categoryMap[categoryId] || categoryId)
      .filter(Boolean)
    : categoryNames;
  const builderDimensions = normalizeConfigurationDimensions(
    builderConfigurationMeta?.dimensions,
    {
      calculationMode:
        builderConfigurationMeta?.dimensions?.calculationMode ||
        productCalculationMode,
      requiredInputs:
        builderConfigurationMeta?.dimensions?.fields ||
        product.requiredInputs,
    }
  );
  const builderDimensionLabels = getDimensionSummaryLabels(builderDimensions);
  const builderCalculationLabels = getCalculationSummaryLabels(builderDimensions);
  const selectedSupplierCount = new Set(
    mixedComponentPricingRows
      .map((component) => component.selectedSupplierId)
      .filter(Boolean)
  ).size;
  const builderUpdatedAt =
    selectedConfiguration?.updatedAt ||
    selectedConfiguration?.savedAt ||
    product.updatedAt;
  const getConfigurationStatusLabel = (configuration) => {
    if (configuration?.recommended) {
      return 'Recommended';
    }

    if (configuration?.isDeleted === true) {
      return 'Trash';
    }

    if (configuration?.status === 'draft') {
      return 'Draft';
    }

    return 'Active';
  };

  const buildCurrentConfigurationSnapshot = (
    savedAt = new Date(),
    configurationMeta = selectedConfigurationDraft
  ) => {
    const snapshot = buildMixedProductConfigurationSnapshot({
      productId: id,
      product,
      dimensions: configurationMeta?.dimensions,
      components: activeProductComponents,
      componentSuppliersByComponentId: filteredComponentSuppliersByComponentId,
      expenses: extraExpenses,
      getSupplierDisplayName,
    });
    const categoryIds = normalizeIdArray(configurationMeta?.categoryIds);

    return {
      ...snapshot,
      product: {
        ...snapshot.product,
        categories: categoryIds.length > 0
          ? categoryIds
          : snapshot.product.categories,
      },
      capturedAt: savedAt.toISOString(),
    };
  };

  const buildConfigurationBundlePayload = (
    configuration,
    overrides = {}
  ) => ({
    productId: id,
    name: configuration.name || productName,
    description: configuration.description || '',
    status: configuration.status || 'active',
    notes: configuration.notes || '',
    savedAt: configuration.savedAt || new Date(),
    snapshotVersion:
      configuration.snapshotVersion || CONFIGURATION_SNAPSHOT_VERSION,
    configurationSnapshot: configuration.configurationSnapshot || null,
    duplicatedFromId: configuration.duplicatedFromId || '',
    ...overrides,
  });

  const handleSaveConfiguration = async () => {
    if (!isAdmin || !isMixedProduct || isSavingConfiguration) {
      return;
    }

    if (activeProductComponents.length === 0) {
      setPageError('Add at least one active component before saving a configuration.');
      setPageMessage('');
      return;
    }

    const savedAt = new Date();
    const configurationName = `${productName} Configuration ${formatDate(savedAt)}`;
    const selectedConfiguration = productConfigurations.find(
      (configuration) => configuration.id === selectedConfigurationId
    );
    const selectedMeta = selectedConfigurationDraft || (
      selectedConfiguration
        ? {
            id: selectedConfiguration.id,
            name: selectedConfiguration.name,
            categoryIds: selectedConfiguration.categoryIds,
            dimensions: selectedConfiguration.dimensions,
            recommended: selectedConfiguration.recommended,
          }
        : null
    );

    setIsSavingConfiguration(true);

    try {
      let savedConfigurationId = selectedConfiguration?.id || '';

      if (selectedConfiguration) {
        await updateBundle(
          selectedConfiguration.id,
          buildConfigurationBundlePayload(selectedConfiguration, {
            name: selectedMeta?.name || selectedConfiguration.name,
            status: 'active',
            savedAt,
            snapshotVersion: CONFIGURATION_SNAPSHOT_VERSION,
            dimensions: selectedMeta?.dimensions,
            configurationSnapshot: buildCurrentConfigurationSnapshot(savedAt, selectedMeta),
          })
        );

        const existingExpenses = await loadExpenseRowsForBundle(selectedConfiguration.id);
        await Promise.all(
          existingExpenses.map((expense) => softDeleteBundleExtraCost(expense.id))
        );
      } else {
        const newBundle = await createBundle({
          productId: id,
          name: selectedMeta?.name || configurationName,
          description: `Saved configuration for ${productName}`,
          status: 'active',
          notes: '',
          isMain: false,
          createdBy: user?.email || user?.displayName || '',
          savedAt,
          snapshotVersion: CONFIGURATION_SNAPSHOT_VERSION,
          dimensions: selectedMeta?.dimensions,
          configurationSnapshot: buildCurrentConfigurationSnapshot(savedAt, selectedMeta),
        });

        savedConfigurationId = newBundle.id;
      }

      await Promise.all(
        extraExpenses.map((expense, index) =>
          createBundleExtraCost({
            bundleId: savedConfigurationId,
            name: expense.name,
            amount: expense.amount,
            costCategory: expense.costCategory || 'extra',
            unit: expense.unit || '',
            sortOrder: expense.sortOrder ?? index,
            notes: expense.notes || '',
          })
        )
      );

      await refreshProductConfigurations(activeProductComponents);
      setSelectedConfigurationId(savedConfigurationId);
      setIsConfigurationBuilderOpen(true);
      setPageMessage('Configuration saved.');
      setPageError('');
    } catch (error) {
      console.error('Error saving configuration:', error);
      setPageError('Failed to save configuration.');
      setPageMessage('');
    } finally {
      setIsSavingConfiguration(false);
    }
  };

  const handleNewConfiguration = () => {
    setConfigurationModalMode('add');
    setConfigurationForm(createEmptyConfigurationForm(product));
    setConfigurationConflict(null);
    setIsSubmittingConfiguration(false);
    setIsConfigurationModalOpen(true);
    setIsConfigurationBuilderOpen(false);
    setSelectedConfigurationId('');
    setSelectedConfigurationDraft(null);
    setPageError('');
    setPageMessage('');
  };

  const handleCloseConfigurationModal = () => {
    if (isSubmittingConfiguration) {
      return;
    }

    setIsConfigurationModalOpen(false);
    setConfigurationConflict(null);
    setConfigurationForm(createEmptyConfigurationForm(product));
    setConfigurationModalMode('add');
  };

  const handleOpenEditConfigurationDetails = () => {
    if (!builderConfigurationMeta) {
      return;
    }

    setConfigurationModalMode('edit');
    setConfigurationForm({
      name: builderConfigurationMeta.name || '',
      categoryId: builderConfigurationMeta.categoryIds?.[0] || '',
      dimensions: createDimensionFormValues(builderConfigurationMeta.dimensions),
    });
    setConfigurationConflict(null);
    setIsSubmittingConfiguration(false);
    setIsConfigurationModalOpen(true);
  };

  const buildConfigurationMetaFromForm = () => ({
    name: configurationForm.name.trim(),
    categoryIds: configurationForm.categoryId ? [configurationForm.categoryId] : [],
    dimensions: validateConfigurationDimensions(configurationForm.dimensions, {
      calculationMode: productCalculationMode,
      requiredInputs: product.requiredInputs,
    }),
    recommended: false,
  });

  const handleConfigurationDimensionChange = (fieldName, value) => {
    setConfigurationForm((prev) => {
      const nextDimensions = {
        ...prev.dimensions,
        [fieldName]: value,
      };
      const previousSuggestion = suggestConfigurationName(
        productName,
        prev.dimensions,
        {
          calculationMode: productCalculationMode,
          requiredInputs: product.requiredInputs,
        }
      );
      const nextSuggestion = suggestConfigurationName(
        productName,
        nextDimensions,
        {
          calculationMode: productCalculationMode,
          requiredInputs: product.requiredInputs,
        }
      );
      const shouldApplySuggestion =
        !prev.name.trim() || prev.name.trim() === previousSuggestion;

      return {
        ...prev,
        dimensions: nextDimensions,
        name: shouldApplySuggestion ? nextSuggestion : prev.name,
      };
    });
    setConfigurationConflict(null);
  };

  const findExistingConfigurationConflict = (configurationMeta, ignoredId = '') => {
    const normalizedName = normalizeComparableText(configurationMeta.name);
    const comparableConfigurations = (Array.isArray(productConfigurations)
      ? productConfigurations
      : []
    ).map((configuration) => ({
      ...configuration,
      dimensions: normalizeConfigurationDimensions(
        configuration.dimensions ||
        configuration.configurationSnapshot?.dimensions,
        {
          calculationMode:
            configuration.dimensions?.calculationMode ||
            configuration.configurationSnapshot?.dimensions?.calculationMode ||
            configuration.configurationSnapshot?.product?.calculationMode ||
            productCalculationMode,
          requiredInputs:
            configuration.dimensions?.fields ||
            configuration.configurationSnapshot?.dimensions?.fields ||
            configuration.configurationSnapshot?.product?.requiredInputs ||
            product.requiredInputs,
        }
      ),
    }));
    const dimensionsMatch = findConfigurationByDimensions(
      comparableConfigurations,
      configurationMeta.dimensions,
      ignoredId
    );

    if (dimensionsMatch) {
      return dimensionsMatch;
    }

    return comparableConfigurations.find((configuration) => {
      if (ignoredId && configuration.id === ignoredId) {
        return false;
      }

      const nameMatches =
        normalizedName &&
        normalizeComparableText(configuration.name) === normalizedName;

      return nameMatches && !getConfigurationDimensionKey(configurationMeta.dimensions);
    }) || null;
  };

  const createConfigurationFromMeta = async (configurationMeta) => {
    const savedAt = new Date();
    const newBundle = await createBundle({
      productId: id,
      name: configurationMeta.name,
      description: `Saved configuration for ${productName}`,
      status: 'draft',
      notes: '',
      isMain: false,
      createdBy: user?.email || user?.displayName || '',
      savedAt,
      snapshotVersion: CONFIGURATION_SNAPSHOT_VERSION,
      dimensions: configurationMeta.dimensions,
      configurationSnapshot: buildCurrentConfigurationSnapshot(savedAt, configurationMeta),
    });
    const nextMeta = {
      ...configurationMeta,
      id: newBundle.id,
    };

    await refreshProductConfigurations(activeProductComponents);
    setSelectedConfigurationId(newBundle.id);
    setSelectedConfigurationDraft(nextMeta);
    setIsConfigurationBuilderOpen(true);
    setIsConfigurationModalOpen(false);
    setConfigurationConflict(null);
    setPageMessage('Configuration created.');
    setPageError('');
  };

  const handleSubmitNewConfiguration = async (event) => {
    event.preventDefault();

    if (isSubmittingConfiguration) {
      return;
    }

    let configurationMeta;

    try {
      configurationMeta = buildConfigurationMetaFromForm();
    } catch (error) {
      setConfigurationConflict({
        type: 'validation',
        message: error?.message || 'Invalid dimensions.',
      });
      return;
    }

    const currentConfigurationId =
      selectedConfigurationDraft?.id || selectedConfigurationId || '';

    if (!configurationMeta.name) {
      setConfigurationConflict({
        type: 'validation',
        message: 'Configuration name is required.',
      });
      return;
    }

    if (configurationModalMode === 'edit') {
      const existingConfiguration = findExistingConfigurationConflict(
        configurationMeta,
        currentConfigurationId
      );

      if (existingConfiguration) {
        setConfigurationConflict({
          type: 'validation',
          message: 'Configuration already exists.',
        });
        return;
      }

      setSelectedConfigurationDraft((prev) => ({
        ...prev,
        ...configurationMeta,
        id: currentConfigurationId,
        recommended:
          prev?.recommended ??
          selectedConfiguration?.recommended ??
          builderConfigurationMeta?.recommended ??
          false,
      }));
      setIsConfigurationModalOpen(false);
      setConfigurationConflict(null);
      setPageMessage('Configuration details updated.');
      setPageError('');
      return;
    }

    const existingConfiguration = findExistingConfigurationConflict(configurationMeta);

    if (existingConfiguration) {
      setConfigurationConflict({
        type: 'duplicate',
        message: 'Configuration already exists.',
        configuration: existingConfiguration,
        configurationMeta,
      });
      return;
    }

    setIsSubmittingConfiguration(true);

    try {
      await createConfigurationFromMeta(configurationMeta);
    } catch (error) {
      console.error('Error creating configuration:', error);
      setConfigurationConflict({
        type: 'validation',
        message: 'Failed to create configuration.',
      });
    } finally {
      setIsSubmittingConfiguration(false);
    }
  };

  const handleOpenExistingConfiguration = () => {
    const existingConfiguration = configurationConflict?.configuration;

    if (!existingConfiguration) {
      return;
    }

    setIsConfigurationModalOpen(false);
    setConfigurationConflict(null);
    handleLoadConfiguration(existingConfiguration);
  };

  const handleCloseConfigurationBuilder = () => {
    setIsConfigurationBuilderOpen(false);
    setSelectedConfigurationId('');
    setSelectedConfigurationDraft(null);
    setIsHistoryExpanded(false);
    setPageError('');
    setPageMessage('');
  };

  const replaceWorkingExpensesFromBundle = async (bundleId) => {
    const [currentWorkingExpenses, sourceExpenses] = await Promise.all([
      loadExpenseRowsForBundle(getWorkingExpenseBundleId(id)),
      loadExpenseRowsForBundle(bundleId),
    ]);

    await Promise.all(
      currentWorkingExpenses.map((expense) =>
        softDeleteBundleExtraCost(expense.id)
      )
    );
    await Promise.all(
      sourceExpenses.map((expense, index) =>
        createBundleExtraCost({
          bundleId: getWorkingExpenseBundleId(id),
          name: expense.name,
          amount: expense.amount,
          costCategory: expense.costCategory || 'extra',
          unit: expense.unit || '',
          sortOrder: expense.sortOrder ?? index,
          notes: expense.notes || '',
        })
      )
    );

    await refreshExtraExpenses();
  };

  const handleLoadConfiguration = async (configuration) => {
    if (
      !isAdmin ||
      !configuration?.id ||
      isConfigurationActionRunning
    ) {
      return;
    }

    setIsConfigurationBuilderOpen(true);
    setSelectedConfigurationId(configuration.id);
    setSelectedConfigurationDraft({
      id: configuration.id,
      name: configuration.name,
      categoryIds: configuration.categoryIds,
      dimensions: configuration.dimensions,
      recommended: configuration.recommended,
    });

    const snapshotComponents = getSnapshotComponents(
      configuration.configurationSnapshot
    );
    const savedSelections = snapshotComponents.filter(
      (component) => component?.selectedComponentSupplierId
    );

    if (savedSelections.length === 0) {
      setPageError('This configuration has no saved component selections.');
      setPageMessage('');
      return;
    }

    const activeComponentsById = activeProductComponents.reduce(
      (lookup, component) => ({
        ...lookup,
        [component.id]: component,
      }),
      {}
    );

    setConfigurationAction({ type: 'load', id: configuration.id });

    try {
      const results = await Promise.allSettled(
        savedSelections.map((snapshotComponent) => {
          const currentComponent = activeComponentsById[snapshotComponent.id];

          if (!currentComponent) {
            return Promise.reject(new Error('Component no longer exists.'));
          }

          return updateSelectedComponentSupplier(
            currentComponent.id,
            snapshotComponent.selectedComponentSupplierId
          );
        })
      );
      const loadedCount = results.filter(
        (result) => result.status === 'fulfilled'
      ).length;
      const failedCount = results.length - loadedCount;

      await refreshProductComponents(componentFilter);
      await replaceWorkingExpensesFromBundle(configuration.id);
      setPageMessage(
        failedCount > 0
          ? `Configuration opened with ${loadedCount} selection(s); ${failedCount} could not be restored.`
          : `Configuration opened. ${loadedCount} selection(s) restored.`
      );
      setPageError(
        loadedCount === 0
          ? 'No saved selections could be restored.'
          : ''
      );
    } catch (error) {
      console.error('Error opening configuration:', error);
      setPageError('Failed to open configuration.');
      setPageMessage('');
    } finally {
      setConfigurationAction({ type: '', id: '' });
    }
  };

  const handleDuplicateConfiguration = async (configuration) => {
    if (
      !isAdmin ||
      !configuration?.id ||
      isConfigurationActionRunning
    ) {
      return;
    }

    const savedAt = new Date();
    const snapshot =
      configuration.configurationSnapshot ||
      buildCurrentConfigurationSnapshot(savedAt);

    setConfigurationAction({ type: 'duplicate', id: configuration.id });

    try {
      const newBundle = await createBundle({
        ...buildConfigurationBundlePayload(configuration, {
          name: `${configuration.name || productName} Copy`,
          status: 'draft',
          isMain: false,
          createdBy: user?.email || user?.displayName || configuration.createdBy || '',
          savedAt,
          snapshotVersion:
            configuration.snapshotVersion || CONFIGURATION_SNAPSHOT_VERSION,
          dimensions: configuration.dimensions,
          configurationSnapshot: snapshot,
          duplicatedFromId: configuration.id,
        }),
      });
      const sourceExpenses = await loadExpenseRowsForBundle(configuration.id);

      await Promise.all(
        sourceExpenses.map((expense, index) =>
          createBundleExtraCost({
            bundleId: newBundle.id,
            name: expense.name,
            amount: expense.amount,
            costCategory: expense.costCategory || 'extra',
            unit: expense.unit || '',
            sortOrder: expense.sortOrder ?? index,
            notes: expense.notes || '',
          })
        )
      );

      await refreshProductConfigurations(activeProductComponents);
      setPageMessage('Configuration duplicated.');
      setPageError('');
    } catch (error) {
      console.error('Error duplicating configuration:', error);
      setPageError('Failed to duplicate configuration.');
      setPageMessage('');
    } finally {
      setConfigurationAction({ type: '', id: '' });
    }
  };

  const handleMoveConfigurationToTrash = async (configuration) => {
    if (
      !isAdmin ||
      !configuration?.id ||
      configuration.isDeleted === true ||
      isConfigurationActionRunning
    ) {
      return;
    }

    setConfigurationAction({ type: 'trash', id: configuration.id });

    try {
      await softDeleteBundle(configuration.id);

      if (selectedConfigurationId === configuration.id) {
        handleCloseConfigurationBuilder();
      }

      await refreshProductConfigurations(activeProductComponents);
      setPageMessage('Configuration moved to trash.');
      setPageError('');
    } catch (error) {
      console.error('Error moving configuration to trash:', error);
      setPageError('Failed to move configuration to trash.');
      setPageMessage('');
    } finally {
      setConfigurationAction({ type: '', id: '' });
    }
  };

  const handleRestoreConfiguration = async (configuration) => {
    if (
      !isAdmin ||
      !configuration?.id ||
      configuration.isDeleted !== true ||
      isConfigurationActionRunning
    ) {
      return;
    }

    setConfigurationAction({ type: 'restore', id: configuration.id });

    try {
      await restoreBundle(configuration.id);
      await refreshProductConfigurations(activeProductComponents);
      setPageMessage('Configuration restored.');
      setPageError('');
    } catch (error) {
      console.error('Error restoring configuration:', error);
      setPageError('Failed to restore configuration.');
      setPageMessage('');
    } finally {
      setConfigurationAction({ type: '', id: '' });
    }
  };

  const handlePermanentlyDeleteConfiguration = async (configuration) => {
    if (
      !isAdmin ||
      !configuration?.id ||
      configuration.isDeleted !== true ||
      isConfigurationActionRunning
    ) {
      return;
    }

    setConfigurationAction({ type: 'delete', id: configuration.id });

    try {
      await deleteBundle(configuration.id);
      await refreshProductConfigurations(activeProductComponents);
      setPageMessage('Configuration permanently deleted.');
      setPageError('');
    } catch (error) {
      console.error('Error permanently deleting configuration:', error);
      setPageError('Failed to permanently delete configuration.');
      setPageMessage('');
    } finally {
      setConfigurationAction({ type: '', id: '' });
    }
  };

  const handleSetMainConfiguration = async (configuration) => {
    if (
      !isAdmin ||
      !configuration?.id ||
      isConfigurationActionRunning
    ) {
      return;
    }

    setConfigurationAction({ type: 'main', id: configuration.id });

    try {
      await Promise.all(
        productConfigurations
          .filter((currentConfiguration) =>
            currentConfiguration.id === configuration.id ||
            currentConfiguration.isMain === true
          )
          .map((currentConfiguration) =>
            updateBundle(
              currentConfiguration.id,
              buildConfigurationBundlePayload(currentConfiguration, {
                isMain: currentConfiguration.id === configuration.id,
              })
            )
          )
      );

      await refreshProductConfigurations(activeProductComponents);
      setPageMessage('Recommended configuration updated.');
      setPageError('');
    } catch (error) {
      console.error('Error setting recommended configuration:', error);
      setPageError('Failed to set recommended configuration.');
      setPageMessage('');
    } finally {
      setConfigurationAction({ type: '', id: '' });
    }
  };

  const handleSubmitSupplierPrice = async (event) => {
    event.preventDefault();

    if (!selectedSupplierId) {
      setSupplierPriceError('Supplier is required.');
      return;
    }

    const selectedActiveVariantName = getActiveVariantName();

    if (product?.type === 'variant' && !selectedActiveVariantName) {
      setSupplierPriceError('Variant is required.');
      return;
    }

    if (String(price).trim() === '') {
      setSupplierPriceError('Price is required.');
      return;
    }

    const parsedPrice = Number(price);

    if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
      setSupplierPriceError('Price must be a positive number.');
      return;
    }

    const newEntry = {
      price: parsedPrice,
      pricingMethod,
      date: new Date(),
      note: notes.trim(),
    };

    setIsSubmittingSupplierPrice(true);
    setSupplierPriceError('');

    try {
      if (product?.type === 'variant') {
        const productSupplier = await addProductSupplier(id, selectedSupplierId, {
          pricingMethod,
        });
        const variantForSupplier = (Array.isArray(productVariants) ? productVariants : [])
          .find((variant) => {
            const productSupplierMap = buildProductSupplierMap(productSuppliers);
            const supplierId = getSupplierIdForVariant(variant, productSupplierMap);

            return (
              supplierId === selectedSupplierId &&
              getVariantName(variant) === selectedActiveVariantName
            );
          });
        const targetVariant = variantForSupplier || await createVariant(id, {
          variantName: selectedActiveVariantName,
          specs: {},
          notes: notes.trim(),
          productId: id,
          supplierId: selectedSupplierId,
          productSupplierId: productSupplier?.id || '',
        });

        await setVariantPrice(targetVariant.id, newEntry);
      } else {
        await createOrUpdateProductSupplier(
          id,
          selectedSupplierId,
          newEntry
        );
      }

      await refreshProductPricingData();
      setIsSupplierPriceModalOpen(false);
      setPageMessage(
        product?.type === 'variant'
          ? 'Variant price saved.'
          : 'Supplier price saved.'
      );
      setPageError('');
      resetSupplierPriceForm();
    } catch (error) {
      console.error('Error saving supplier price:', error);
      setSupplierPriceError('Failed to save supplier price.');
      setIsSubmittingSupplierPrice(false);
    }
  };

  const handleSubmitAddSupplier = async (event) => {
    event.preventDefault();

    if (isAddingSupplier) {
      return;
    }

    if (!selectedSupplierId) {
      setAddSupplierError('Supplier is required.');
      return;
    }

    setIsAddingSupplier(true);
    setAddSupplierError('');

    try {
      await addProductSupplier(id, selectedSupplierId, {
        pricingMethod: DEFAULT_PRICING_METHOD,
      });
      await refreshProductPricingData();
      setIsAddSupplierOpen(false);
      setPageMessage('Supplier link added.');
      setPageError('');
      resetAddSupplierForm();
    } catch (error) {
      console.error('Error adding supplier link:', error);
      setAddSupplierError('Failed to add supplier link.');
      setIsAddingSupplier(false);
    }
  };

  const handleAddAllowedVariant = () => {
    const nextVariant = String(editForm.variantDraft || '').trim();

    if (!nextVariant) {
      setEditError('Variant name is required.');
      return;
    }

    const currentVariants = normalizeVariantNames(editForm.allowedVariants);

    if (currentVariants.includes(nextVariant)) {
      setEditError('Variant already exists.');
      return;
    }

    setEditForm((prev) => ({
      ...prev,
      allowedVariants: [...currentVariants, nextVariant],
      variantDraft: '',
    }));
    setEditError('');
  };

  const handleRemoveAllowedVariant = (variantName) => {
    setEditForm((prev) => ({
      ...prev,
      allowedVariants: normalizeVariantNames(prev.allowedVariants).filter(
        (item) => item !== variantName
      ),
    }));
    setEditError('');
  };

  const handleSubmitEditProduct = async (event) => {
    event.preventDefault();

    const trimmedName = editForm.name.trim();

    if (!trimmedName) {
      setEditError('Name is required.');
      return;
    }

    const productType = normalizeEditableProductType(editForm.type);
    const nextAllowedVariants = normalizeVariantNames(editForm.allowedVariants);
    const nextRequiredInputs =
      productType === 'mixed'
        ? normalizeMixedProductRequiredInputs(editForm.requiredInputs)
        : [];
    const nextCalculationMode =
      productType === 'mixed'
        ? normalizeMixedProductCalculationMode(editForm.calculationMode)
        : 'fixed';
    const missingCalculationInputs =
      getMixedProductCalculationModeRequiredInputs(nextCalculationMode).filter(
        (input) => !nextRequiredInputs.includes(input)
      );

    if (productType === 'variant' && String(editForm.variantDraft || '').trim()) {
      setEditError('Add or clear the pending variant name.');
      return;
    }

    if (productType === 'variant' && nextAllowedVariants.length === 0) {
      setEditError('Add at least one allowed variant.');
      return;
    }

    if (productType === 'mixed' && missingCalculationInputs.length > 0) {
      setEditError(
        `${getMixedProductCalculationModeLabel(nextCalculationMode)} requires ${missingCalculationInputs
          .map((input) => getMixedProductRequiredInputLabel(input))
          .join(', ')}.`
      );
      return;
    }

    const payload = {
      name: trimmedName,
      description: editForm.description.trim(),
      type: productType,
      allowedVariants: nextAllowedVariants,
      requiredInputs: nextRequiredInputs,
      calculationMode: nextCalculationMode,
      categories: normalizeIdArray(editForm.categories),
      isDeleted: product.isDeleted === true,
      deletedAt: product.deletedAt ?? null,
    };

    setIsSubmittingEdit(true);
    setEditError('');

    try {
      const updatedProduct = await updateProduct(id, payload);

      setProduct(updatedProduct);
      setEditForm(buildEditFormFromProduct(updatedProduct));
      setIsEditModalOpen(false);
      setPageMessage('Product updated.');
      setPageError('');
      setIsSubmittingEdit(false);
    } catch (error) {
      console.error('Error updating product:', error);
      setEditError('Failed to update product.');
      setIsSubmittingEdit(false);
    }
  };

  const handleConfirmRemoveSupplier = async () => {
    if (isRemovingSupplier || !supplierPendingRemoval?.supplierId) {
      return;
    }

    setIsRemovingSupplier(true);

    try {
      if (supplierPendingRemoval.removalType === 'variantSupplier') {
        const currentProductSupplierMap = buildProductSupplierMap(productSuppliers);
        const supplierVariants = (Array.isArray(productVariants) ? productVariants : [])
          .filter((variant) =>
            getSupplierIdForVariant(variant, currentProductSupplierMap) ===
            supplierPendingRemoval.supplierId
          );

        await Promise.all(
          supplierVariants
            .filter((variant) => variant?.id)
            .map((variant) => deleteVariant(variant.id))
        );
      }

      await deleteProductSupplier(id, supplierPendingRemoval.supplierId);
      await refreshProductPricingData();
      setIsRemoveModalOpen(false);
      setSupplierPendingRemoval(null);
      setPageMessage('Supplier link removed.');
      setPageError('');
      setIsRemovingSupplier(false);
    } catch (error) {
      console.error('Error removing supplier pricing:', error);
      setPageError('Failed to remove supplier pricing.');
      setIsRemovingSupplier(false);
    }
  };

  const handleOpenAddPriceModal = (supplierOrRow = '') => {
    resetSupplierPriceForm();
    if (typeof supplierOrRow === 'object' && supplierOrRow !== null) {
      setSelectedSupplierId(supplierOrRow.supplierId || '');
      setPricingMethod(normalizeEditablePricingMethod(supplierOrRow.pricingMethod));
    } else {
      setSelectedSupplierId(supplierOrRow);
    }
    setIsSupplierPriceModalOpen(true);
  };

  const handleOpenAddVariantPriceForRow = (row) => {
    resetSupplierPriceForm();
    setSelectedSupplierId(row?.supplierId || '');
    setPricingMethod(normalizeEditablePricingMethod(row?.pricingMethod));
    setIsSupplierPriceModalOpen(true);
  };

  const handleToggleComponentExpanded = async (component) => {
    if (!component?.id) {
      return;
    }

    const isExpanded = expandedComponentIds.includes(component.id);

    if (isExpanded) {
      setExpandedComponentIds((prev) =>
        prev.filter((componentId) => componentId !== component.id)
      );
      setMixedSupplierSearch('');
      return;
    }

    setExpandedComponentIds([component.id]);
    setMixedSupplierSearch('');

    if (
      !Object.prototype.hasOwnProperty.call(
        componentSuppliersByComponentId,
        component.id
      )
    ) {
      try {
        await refreshComponentSupplierPricing(component.id);
      } catch (error) {
        console.error('Error loading component supplier pricing:', error);
        setPageError('Failed to load component supplier pricing.');
      }
    }
  };

  const handleSelectComponentSupplier = async (
    component,
    componentSupplier
  ) => {
    if (
      !component?.id ||
      !componentSupplier?.id ||
      componentSelectionUpdatingByComponentId[component.id] === true
    ) {
      return;
    }

    if (!hasValidComponentSupplierPrice(componentSupplier)) {
      setPageError('Supplier row has no valid current price.');
      return;
    }

    if (isSupplierDeletedOrMissing(componentSupplier.supplierId)) {
      setPageError('Deleted suppliers cannot be selected.');
      return;
    }

    const previousComponents = productComponents;
    const previousActiveComponents = activeProductComponents;
    const nextPricing = calculateComponentPricing(
      {
        ...component,
        selectedComponentSupplierId: componentSupplier.id,
        selectedSupplierId: componentSupplier.supplierId,
      },
      getConfigurationComponentSupplierRows(
        component,
        componentSuppliersByComponentId[component.id] || []
      ),
      activeBuilderDimensions
    );

    setComponentSelectionUpdatingByComponentId((prev) => ({
      ...prev,
      [component.id]: true,
    }));
    setProductComponents((prev) =>
      prev.map((currentComponent) =>
        currentComponent.id === component.id
          ? {
            ...currentComponent,
            selectedComponentSupplierId: nextPricing.selectedComponentSupplierId,
            selectedSupplierId: nextPricing.selectedSupplierId,
            selectedUnitPrice: nextPricing.selectedUnitPrice,
            lineTotal: nextPricing.lineTotal,
          }
        : currentComponent
      )
    );
    setActiveProductComponents((prev) =>
      prev.map((currentComponent) =>
        currentComponent.id === component.id
          ? {
            ...currentComponent,
            selectedComponentSupplierId: nextPricing.selectedComponentSupplierId,
            selectedSupplierId: nextPricing.selectedSupplierId,
            selectedUnitPrice: nextPricing.selectedUnitPrice,
            lineTotal: nextPricing.lineTotal,
          }
          : currentComponent
      )
    );

    try {
      const updatedComponent = await updateSelectedComponentSupplier(
        component.id,
        componentSupplier.id
      );

      if (updatedComponent) {
        setProductComponents((prev) =>
          prev.map((currentComponent) =>
            currentComponent.id === updatedComponent.id
              ? updatedComponent
              : currentComponent
          )
        );
        setActiveProductComponents((prev) =>
          prev.map((currentComponent) =>
            currentComponent.id === updatedComponent.id
              ? updatedComponent
              : currentComponent
          )
        );
      }

      setPageError('');
      setPageMessage('Component supplier selected.');
    } catch (error) {
      console.error('Error selecting component supplier:', error);
      setProductComponents(previousComponents);
      setActiveProductComponents(previousActiveComponents);
      setPageError(error?.message || 'Failed to select component supplier.');
    } finally {
      setComponentSelectionUpdatingByComponentId((prev) => ({
        ...prev,
        [component.id]: false,
      }));
    }
  };

  const handleOpenComponentHistoryModal = async (component, componentSupplier) => {
    if (!componentSupplier?.id) {
      return;
    }

    setSelectedComponentHistory([]);
    setSelectedComponentHistoryTitle(
      `${component?.name || 'Component'} - ${getSupplierDisplayName(
        componentSupplier.supplierId
      )}`
    );
    setIsLoadingComponentHistory(true);
    setIsComponentHistoryModalOpen(true);

    if (
      componentSupplier.sourceType === 'productSupplier' ||
      componentSupplier.sourceType === 'productVariant'
    ) {
      setSelectedComponentHistory(
        sortPriceHistoryByDateDesc(
          getSafePriceHistory(componentSupplier.priceHistory)
        )
      );
      setIsLoadingComponentHistory(false);
      return;
    }

    try {
      const history = await getComponentPriceHistory(componentSupplier.id);

      setSelectedComponentHistory(Array.isArray(history) ? history : []);
    } catch (error) {
      console.error('Error loading component price history:', error);
      setPageError('Failed to load component price history.');
    } finally {
      setIsLoadingComponentHistory(false);
    }
  };

  const handleCloseComponentHistoryModal = () => {
    setIsComponentHistoryModalOpen(false);
    setSelectedComponentHistory([]);
    setSelectedComponentHistoryTitle('');
    setIsLoadingComponentHistory(false);
  };

  const resetComponentForm = () => {
    setComponentForm(createEmptyComponentForm(id));
    setComponentProductSearch('');
    setHighlightedComponentProductIndex(0);
    setSelectedComponent(null);
    setComponentError('');
    setIsSubmittingComponent(false);
  };

  const ensureSourceProductVariants = async (sourceProductId) => {
    const normalizedSourceProductId = String(sourceProductId || '').trim();

    if (!normalizedSourceProductId) {
      return [];
    }

    if (sourceProductVariantsByProductId[normalizedSourceProductId]) {
      return sourceProductVariantsByProductId[normalizedSourceProductId];
    }

    const sourceProductVariants = await getActiveProductVariantsByProductId(
      normalizedSourceProductId
    );
    const safeSourceProductVariants = Array.isArray(sourceProductVariants)
      ? sourceProductVariants
      : [];

    setSourceProductVariantsByProductId((prev) => ({
      ...prev,
      [normalizedSourceProductId]: safeSourceProductVariants,
    }));

    return safeSourceProductVariants;
  };

  const handleOpenAddComponentModal = () => {
    setComponentModalMode('add');
    setComponentForm(createEmptyComponentForm(id));
    setComponentProductSearch('');
    setHighlightedComponentProductIndex(0);
    setSelectedComponent(null);
    setComponentError('');
    setIsComponentModalOpen(true);
  };

  const handleOpenEditComponentModal = async (component) => {
    setComponentModalMode('edit');
    setSelectedComponent(component);
    setComponentForm({
      productId: component?.productId || id,
      sourceProductId: component?.sourceProductId || '',
      sourceProductName: component?.sourceProductName || '',
      sourceVariantId: component?.sourceVariantId || '',
      sourceVariantName: component?.sourceVariantName || '',
      name: component?.name || '',
      description: component?.description || '',
      quantity: String(component?.quantity ?? 1),
      unit: component?.unit || '',
      sortOrder: String(component?.sortOrder ?? 0),
      isRequired: component?.isRequired === true,
    });
    setComponentError('');
    setIsComponentModalOpen(true);

    if (component?.sourceProductId) {
      try {
        await ensureSourceProductVariants(component.sourceProductId);
      } catch (error) {
        console.error('Error loading source product variants:', error);
        setComponentError('Failed to load source product variants.');
      }
    }
  };

  const handleCloseComponentModal = () => {
    if (isSubmittingComponent) {
      return;
    }

    setIsComponentModalOpen(false);
    resetComponentForm();
  };

  const handleComponentFormChange = (fieldName, value) => {
    setComponentForm((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    if (componentError) {
      setComponentError('');
    }
  };

  const hasDuplicateComponentInConfiguration = (
    candidate,
    ignoredComponentId = ''
  ) => {
    const sourceProductId = normalizeSnapshotString(candidate?.sourceProductId);
    const sourceVariantIdentity = getComponentVariantIdentity(candidate);

    if (!sourceProductId) {
      return false;
    }

    return activeProductComponents.some((component) => {
      if (
        !component ||
        component.isDeleted === true ||
        (ignoredComponentId && component.id === ignoredComponentId)
      ) {
        return false;
      }

      return (
        normalizeSnapshotString(component.sourceProductId) === sourceProductId &&
        getComponentVariantIdentity(component) === sourceVariantIdentity
      );
    });
  };

  const handleSelectComponentProduct = async (sourceProduct) => {
    if (!sourceProduct?.id) {
      return;
    }

    const nextComponent = {
      sourceProductId: sourceProduct.id,
      sourceVariantId: '',
      sourceVariantName: '',
    };

    if (
      sourceProduct.type !== 'variant' &&
      hasDuplicateComponentInConfiguration(nextComponent, selectedComponent?.id)
    ) {
      setComponentError(DUPLICATE_COMPONENT_MESSAGE);
      return;
    }

    setComponentForm((prev) => ({
      ...prev,
      sourceProductId: sourceProduct.id,
      sourceProductName: sourceProduct.name || '',
      sourceVariantId: '',
      sourceVariantName: '',
      name: sourceProduct.name || '',
      description: sourceProduct.description || '',
      unit: prev.unit,
    }));
    setComponentError('');

    if (sourceProduct.type === 'variant') {
      try {
        await ensureSourceProductVariants(sourceProduct.id);
      } catch (error) {
        console.error('Error loading source product variants:', error);
        setComponentError('Failed to load source product variants.');
      }
    }
  };

  const handleSelectComponentVariant = (variantName) => {
    const selectedVariantOption = sourceVariantOptions.find(
      (option) => option.name === variantName
    );
    const nextComponent = {
      ...componentForm,
      sourceVariantId: selectedVariantOption?.id || variantName,
      sourceVariantName: variantName,
    };

    if (
      variantName &&
      hasDuplicateComponentInConfiguration(nextComponent, selectedComponent?.id)
    ) {
      setComponentError(DUPLICATE_COMPONENT_MESSAGE);
      return;
    }

    setComponentForm((prev) => ({
      ...prev,
      sourceVariantId: selectedVariantOption?.id || variantName,
      sourceVariantName: variantName,
    }));
    setComponentError('');
  };

  const getNextComponentSortOrder = () => {
    const sortOrders = (Array.isArray(productComponents) ? productComponents : [])
      .map((component) => Number(component?.sortOrder))
      .filter((sortOrder) => Number.isFinite(sortOrder));

    return sortOrders.length > 0 ? Math.max(...sortOrders) + 1 : 1;
  };

  const handleComponentProductSearchKeyDown = (event) => {
    if (filteredComponentProductOptions.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedComponentProductIndex((currentIndex) =>
        Math.min(currentIndex + 1, filteredComponentProductOptions.length - 1)
      );
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedComponentProductIndex((currentIndex) =>
        Math.max(currentIndex - 1, 0)
      );
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const selectedProduct =
        filteredComponentProductOptions[highlightedComponentProductIndex] ||
        filteredComponentProductOptions[0];

      handleSelectComponentProduct(selectedProduct);
    }
  };

  const handleSubmitComponent = async (event) => {
    event.preventDefault();

    const componentName = componentForm.name.trim();
    const quantity = Number(componentForm.quantity);
    const sortOrder =
      componentModalMode === 'add'
        ? getNextComponentSortOrder()
        : Number(componentForm.sortOrder);

    if (componentModalMode === 'add' && !componentForm.sourceProductId) {
      setComponentError('Select a product to add as a component.');
      return;
    }

    if (isSourceVariantSelectionMissing) {
      setComponentError('Select a variant for this component product.');
      return;
    }

    if (!componentName) {
      setComponentError('Component product is required.');
      return;
    }

    if (!Number.isFinite(quantity) || quantity <= 0) {
      setComponentError('Quantity must be a positive number.');
      return;
    }

    if (componentModalMode === 'edit' && !Number.isFinite(sortOrder)) {
      setComponentError('Sort order must be a number.');
      return;
    }

    if (
      hasDuplicateComponentInConfiguration(
        componentForm,
        componentModalMode === 'edit' ? selectedComponent?.id : ''
      )
    ) {
      setComponentError(DUPLICATE_COMPONENT_MESSAGE);
      return;
    }

    const payload = {
      productId: id,
      name: componentName,
      description: componentForm.description.trim(),
      quantity,
      unit: componentForm.unit.trim(),
      sourceProductId: componentForm.sourceProductId,
      sourceProductName: componentForm.sourceProductName || componentName,
      sourceVariantId: componentForm.sourceVariantId,
      sourceVariantName: componentForm.sourceVariantName,
      sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
      isRequired: componentForm.isRequired === true,
    };

    setIsSubmittingComponent(true);
    setComponentError('');

    try {
      if (componentModalMode === 'edit' && selectedComponent?.id) {
        await updateProductComponent(selectedComponent.id, payload);
        setPageMessage('Component updated.');
      } else {
        await createProductComponent(payload);
        setPageMessage('Component added.');
      }

      await refreshProductComponents(componentFilter);
      setPageError('');
      setIsComponentModalOpen(false);
      resetComponentForm();
    } catch (error) {
      console.error('Error saving component:', error);
      setComponentError('Failed to save component.');
      setIsSubmittingComponent(false);
    }
  };

  const resetExpenseForm = () => {
    setExpenseForm(createEmptyExpenseForm());
    setSelectedExpense(null);
    setExpenseError('');
    setIsSubmittingExpense(false);
    setExpenseModalMode('add');
  };

  const handleOpenAddExpenseModal = () => {
    setExpenseModalMode('add');
    setExpenseForm(createEmptyExpenseForm());
    setSelectedExpense(null);
    setExpenseError('');
    setIsExpenseModalOpen(true);
  };

  const handleOpenEditExpenseModal = (expense) => {
    setExpenseModalMode('edit');
    setSelectedExpense(expense);
    setExpenseForm({
      name: expense?.name || '',
      amount: String(expense?.amount ?? ''),
    });
    setExpenseError('');
    setIsExpenseModalOpen(true);
  };

  const handleCloseExpenseModal = () => {
    if (isSubmittingExpense) {
      return;
    }

    setIsExpenseModalOpen(false);
    resetExpenseForm();
  };

  const handleExpenseFormChange = (fieldName, value) => {
    setExpenseForm((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    if (expenseError) {
      setExpenseError('');
    }
  };

  const handleSubmitExpense = async (event) => {
    event.preventDefault();

    const expenseName = expenseForm.name.trim();
    const amount = Number(expenseForm.amount);

    if (!expenseName) {
      setExpenseError('Expense name is required.');
      return;
    }

    if (!Number.isFinite(amount) || amount < 0) {
      setExpenseError('Expense value must be zero or more.');
      return;
    }

    setIsSubmittingExpense(true);
    setExpenseError('');

    try {
      if (expenseModalMode === 'edit' && selectedExpense?.id) {
        await updateBundleExtraCost(selectedExpense.id, {
          bundleId: getWorkingExpenseBundleId(id),
          name: expenseName,
          amount,
          costCategory: selectedExpense.costCategory || 'extra',
          unit: selectedExpense.unit || '',
          sortOrder: selectedExpense.sortOrder ?? 0,
          notes: selectedExpense.notes || '',
        });
        setPageMessage('Expense updated.');
      } else {
        await createBundleExtraCost({
          bundleId: getWorkingExpenseBundleId(id),
          name: expenseName,
          amount,
          costCategory: 'extra',
          unit: '',
          sortOrder: extraExpenses.length,
          notes: '',
        });
        setPageMessage('Expense added.');
      }

      await refreshExtraExpenses();
      setPageError('');
      setIsExpenseModalOpen(false);
      resetExpenseForm();
    } catch (error) {
      console.error('Error saving expense:', error);
      setExpenseError('Failed to save expense.');
      setIsSubmittingExpense(false);
    }
  };

  const handleDeleteExpense = async (expense) => {
    if (isUpdatingExpenseStatus || !expense?.id) {
      return;
    }

    setIsUpdatingExpenseStatus(true);

    try {
      await softDeleteBundleExtraCost(expense.id);
      await refreshExtraExpenses();
      setPageMessage('Expense deleted.');
      setPageError('');
    } catch (error) {
      console.error('Error deleting expense:', error);
      setPageError('Failed to delete expense.');
    } finally {
      setIsUpdatingExpenseStatus(false);
    }
  };

  const handleSoftDeleteComponent = async (component) => {
    if (isUpdatingComponentStatus || !component?.id) {
      return;
    }

    setIsUpdatingComponentStatus(true);

    try {
      await softDeleteProductComponent(component.id);
      await refreshProductComponents(componentFilter);
      setExpandedComponentIds((prev) =>
        prev.filter((componentId) => componentId !== component.id)
      );
      setPageMessage('Component moved to deleted.');
      setPageError('');
    } catch (error) {
      console.error('Error deleting component:', error);
      setPageError('Failed to delete component.');
    } finally {
      setIsUpdatingComponentStatus(false);
    }
  };

  const backButtonStyle = {
    alignSelf: 'flex-start',
  };

  const titleRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  };

  const sectionHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 12,
  };

  const textStyle = {
    margin: 0,
    color: '#334155',
    lineHeight: 1.6,
  };

  const tagsStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    padding: 0,
    margin: 0,
    listStyle: 'none',
  };

  const tableWrapStyle = {
    overflowX: 'auto',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  };

  const tableStyle = {
    width: '100%',
    minWidth: 900,
  };

  const tableHeadCellStyle = {};

  const tableBodyCellStyle = {
    color: '#334155',
    fontSize: 14,
    verticalAlign: 'top',
    cursor: 'default',
  };

  const supplierCellStyle = {
    ...tableBodyCellStyle,
  };

  const supplierRowStyle = {
    cursor: 'default',
  };

  const rowActionsStyle = {};

  const compactSectionStyle = {
    paddingTop: 18,
    paddingBottom: 18,
  };

  const compactSummaryGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
  };

  const compactSummaryItemStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '10px 0',
  };

  const componentStatusStyle = (status) => ({
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    padding: '4px 8px',
    fontSize: 12,
    fontWeight: 700,
    color: status === 'ready' ? '#166534' : '#92400e',
    backgroundColor: status === 'ready' ? '#dcfce7' : '#fef3c7',
    whiteSpace: 'nowrap',
  });

  const historyModalContentStyle = {
    marginTop: 20,
    maxHeight: '60vh',
    overflowY: 'auto',
  };

  const emptyStateStyle = {
    margin: 0,
    color: '#64748b',
    lineHeight: 1.6,
    padding: '16px',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    backgroundColor: '#f8fafc',
  };

  const emptyStateTitleStyle = {
    margin: 0,
    color: '#0f172a',
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.4,
  };

  const emptyStateDetailStyle = {
    margin: '4px 0 0',
    color: '#64748b',
    fontSize: 13,
    lineHeight: 1.5,
  };

  const configurationSummaryStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  };

  const configurationSummaryMainStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minWidth: 0,
    flex: '1 1 320px',
  };

  const configurationSummaryMetaStyle = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  };

  const configurationSummaryTitleStyle = {
    margin: 0,
    color: '#0f172a',
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.25,
    overflowWrap: 'anywhere',
  };

  const configurationMetaTextStyle = {
    color: '#64748b',
    fontSize: 13,
    lineHeight: 1.4,
  };

  const supplierSelectionGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
    gap: 10,
  };

  const supplierSelectionCardStyle = {
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: 10,
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 0,
  };

  const componentCardHeaderStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  };

  const componentCardTitleStyle = {
    margin: 0,
    color: '#0f172a',
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.35,
    overflowWrap: 'anywhere',
  };

  const componentCardMetaStyle = {
    margin: 0,
    color: '#64748b',
    fontSize: 12,
    lineHeight: 1.35,
  };

  const configurationTotalHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  };

  const configurationTotalValueStyle = {
    color: '#0f172a',
    fontSize: 18,
    fontWeight: 700,
  };

  const configurationTotalRowsStyle = {
    display: 'grid',
    gap: 8,
  };

  const configurationTotalRowStyle = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: 12,
    alignItems: 'center',
    color: '#334155',
    fontSize: 14,
  };

  const configurationSummarySectionStyle = {
    display: 'grid',
    gap: 14,
    padding: 16,
    border: '1px solid #dbe3ee',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
  };

  const configurationSummaryBlockStyle = {
    display: 'grid',
    gap: 8,
  };

  const configurationSummaryBlockTitleStyle = {
    margin: 0,
    color: '#475569',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0,
    textTransform: 'uppercase',
  };

  const configurationSummaryDividerStyle = {
    height: 1,
    backgroundColor: '#e2e8f0',
  };

  const configurationSummaryTotalStyle = {
    ...configurationTotalRowStyle,
    paddingTop: 4,
    color: '#0f172a',
    fontWeight: 800,
  };

  const configurationSummaryGrandTotalStyle = {
    ...configurationTotalRowStyle,
    color: '#0f172a',
    fontSize: 18,
    fontWeight: 800,
  };

  const modalToolbarStyle = {
    display: 'grid',
    gap: 12,
    marginBottom: 16,
  };

  const componentActionMenuStyle = {
    position: 'relative',
    flex: '0 0 auto',
  };

  const componentActionMenuButtonStyle = {
    width: 32,
    height: 32,
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    color: '#475569',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    listStyle: 'none',
    fontSize: 18,
    lineHeight: 1,
  };

  const componentActionMenuListStyle = {
    position: 'absolute',
    right: 0,
    top: 36,
    zIndex: 5,
    minWidth: 160,
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: 4,
    backgroundColor: '#ffffff',
    boxShadow: '0 12px 24px rgba(15, 23, 42, 0.12)',
  };

  const componentActionMenuItemStyle = {
    width: '100%',
    border: 0,
    borderRadius: 6,
    padding: '8px 10px',
    backgroundColor: 'transparent',
    color: '#0f172a',
    font: 'inherit',
    fontSize: 13,
    textAlign: 'left',
    cursor: 'pointer',
  };

  const supplierWorkspacePanelStyle = (isOpen) => ({
    maxHeight: isOpen ? 680 : 0,
    opacity: isOpen ? 1 : 0,
    overflow: 'hidden',
    transition: 'max-height 220ms ease, opacity 180ms ease',
    marginTop: isOpen ? 14 : 0,
  });

  const supplierWorkspacePanelInnerStyle = {
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    backgroundColor: '#f8fafc',
    padding: 12,
  };

  const renderEmptyState = (title, detail = '') => (
    <div style={emptyStateStyle}>
      <p style={emptyStateTitleStyle}>{title}</p>
      {detail ? <p style={emptyStateDetailStyle}>{detail}</p> : null}
    </div>
  );

  const renderTags = (values) => {
    if (values.length === 0) {
      return (
        <ul style={tagsStyle}>
          <li>
            <span className="tag">-</span>
          </li>
        </ul>
      );
    }

    return (
      <ul style={tagsStyle}>
        {values.map((value) => (
          <li key={value}>
            <span className="tag">{value}</span>
          </li>
        ))}
      </ul>
    );
  };

  const fieldWrapStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };

  const labelStyle = {
    fontSize: 14,
    fontWeight: 600,
    color: '#0f172a',
  };

  const inputBaseStyle = {
    width: '100%',
    border: '1px solid #cbd5e1',
    borderRadius: 12,
    padding: '12px 14px',
    fontSize: 14,
    color: '#0f172a',
    backgroundColor: '#ffffff',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
  };

  const helperTextStyle = {
    margin: 0,
    fontSize: 13,
    color: '#64748b',
    lineHeight: 1.5,
  };

  const productTypeDisplayStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  };

  const productTypeTextStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 0,
    flex: '1 1 220px',
  };

  const productTypeLabelStyle = {
    margin: 0,
    color: '#0f172a',
    fontSize: 20,
    fontWeight: 600,
    lineHeight: 1.3,
  };

  const productTypeMetaStyle = {
    margin: 0,
    color: '#64748b',
    fontSize: 13,
    lineHeight: 1.5,
  };

  const overviewGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 14,
  };

  const overviewItemStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 0,
  };

  const overviewLabelStyle = {
    margin: 0,
    color: '#64748b',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0,
  };

  const badgeStyle = (tone = 'neutral') => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'fit-content',
    maxWidth: '100%',
    borderRadius: 999,
    padding: '4px 8px',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.2,
    color:
      tone === 'success'
        ? '#166534'
        : tone === 'warning'
          ? '#92400e'
          : '#475569',
    backgroundColor:
      tone === 'success'
        ? '#bbf7d0'
        : tone === 'warning'
          ? '#fef3c7'
          : '#f1f5f9',
    border:
      tone === 'success'
        ? '1px solid #86efac'
        : tone === 'warning'
          ? '1px solid #fcd34d'
          : '1px solid #e2e8f0',
  });

  return (
    <div className="page product-details-page">
      <StatusMessage tone="success" message={pageMessage} />
      <StatusMessage tone="error" message={pageError} />

      <button
        type="button"
        className="btn btn-secondary"
        style={backButtonStyle}
        onClick={() => navigate('/products')}
      >
        Back to Products
      </button>

      <div className="page-header" style={titleRowStyle}>
        <h1 className="page-title">{productName}</h1>
        {isAdmin ? (
          <div className="page-header__actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleOpenEditModal}
            >
              Edit Product
            </button>
          </div>
        ) : null}
      </div>

      <section className="surface-card surface-card--section">
        <div className="section-header" style={sectionHeaderStyle}>
          <h2 className="section-title">Overview</h2>
        </div>
        <div style={overviewGridStyle}>
          <div style={overviewItemStyle}>
            <p style={overviewLabelStyle}>Description</p>
            <p style={textStyle}>{product.description?.trim() || '-'}</p>
          </div>
          <div style={overviewItemStyle}>
            <p style={overviewLabelStyle}>Product Type</p>
            <div style={productTypeDisplayStyle}>
              <div style={productTypeTextStyle}>
                <p style={productTypeLabelStyle}>{getProductTypeLabel(product.type)}</p>
                <p style={productTypeMetaStyle}>
                  {getProductTypeDescription(product.type, allowedVariantNames.length)}
                </p>
              </div>
            </div>
          </div>
          <div style={overviewItemStyle}>
            <p style={overviewLabelStyle}>Categories</p>
            {renderTags(categoryNames)}
          </div>
          {isVariantProduct ? (
            <div style={overviewItemStyle}>
              <p style={overviewLabelStyle}>Allowed Variants</p>
              {renderTags(allowedVariantNames)}
            </div>
          ) : null}
          {isMixedProduct ? (
            <div style={overviewItemStyle}>
              <p style={overviewLabelStyle}>Required Inputs</p>
              {renderTags(requiredInputLabels)}
            </div>
          ) : null}
          {isMixedProduct ? (
            <div style={overviewItemStyle}>
              <p style={overviewLabelStyle}>Calculation Mode</p>
              <p style={textStyle}>
                {getMixedProductCalculationModeLabel(productCalculationMode)}
              </p>
            </div>
          ) : null}
        </div>
      </section>

      {isMixedProduct ? (
        <section className="surface-card surface-card--section" style={compactSectionStyle}>
          {isAdmin ? (
            <div className="section-header" style={sectionHeaderStyle}>
              <div className="section-actions" style={rowActionsStyle}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleNewConfiguration}
                  disabled={Boolean(configurationAction.type)}
                >
                  New Configuration
                </button>
              </div>
            </div>
          ) : null}

          <div className="controls-bar">
            <div className="controls-bar__filter">
              <div className="toggle-group" aria-label="Configuration library view">
                <button
                  type="button"
                  onClick={() => setConfigurationLibraryView('active')}
                  className={configurationLibraryView === 'active' ? 'active' : ''}
                >
                  Active
                </button>
                <button
                  type="button"
                  onClick={() => setConfigurationLibraryView('trash')}
                  className={configurationLibraryView === 'trash' ? 'active' : ''}
                >
                  Trash
                </button>
              </div>
            </div>

            <div className="controls-bar__search">
              <SearchBar
                value={configurationLibrarySearchTerm}
                onChange={(event) =>
                  setConfigurationLibrarySearchTerm(event.target.value)
                }
                placeholder="Search Configurations"
              />
            </div>
          </div>

          {configurationLibraryRows.length === 0 ? (
            renderEmptyState(
              'No configurations found',
              'Try adjusting search or filters, or create a new configuration.'
            )
          ) : (
            <div className="table-container" style={tableWrapStyle}>
              <table
                className="data-table product-detail-card-table product-detail-configuration-table"
                style={tableStyle}
              >
                <thead>
                  <tr>
                    <th style={tableHeadCellStyle}>Name</th>
                    <th style={tableHeadCellStyle}>Dimensions</th>
                    <th style={tableHeadCellStyle}>Components</th>
                    <th style={tableHeadCellStyle}>Status</th>
                    <th style={tableHeadCellStyle}>Updated</th>
                    {isAdmin ? <th style={tableHeadCellStyle}>Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {configurationLibraryRows.map((configuration) => {
                    const isRowActionRunning =
                      configurationAction.id === configuration.id &&
                      configurationAction.type === 'load';
                    const isSelectedConfiguration =
                      selectedConfigurationId === configuration.id;

                    return (
                      <tr
                        key={configuration.id}
                        className="configuration-library-row"
                        style={{
                          ...supplierRowStyle,
                          backgroundColor: isSelectedConfiguration ? '#f8fafc' : undefined,
                        }}
                      >
                        <td
                          data-label="Name"
                          style={{ ...tableBodyCellStyle, fontWeight: 700, color: '#0f172a' }}
                        >
                          {configuration.name || 'Unnamed configuration'}
                        </td>
                        <td data-label="Dimensions" style={tableBodyCellStyle}>
                          {configuration.dimensionDisplay || '-'}
                        </td>
                        <td data-label="Components" style={tableBodyCellStyle}>
                          {configuration.componentCount || 0}
                        </td>
                        <td data-label="Status" style={tableBodyCellStyle}>
                          <span
                            style={badgeStyle(
                              configuration.recommended ? 'success' : 'neutral'
                            )}
                          >
                            {getConfigurationStatusLabel(configuration)}
                          </span>
                        </td>
                        <td data-label="Updated" style={tableBodyCellStyle}>
                          {formatDate(configuration.updatedAt)}
                        </td>
                        {isAdmin ? (
                          <td data-label="Actions" style={tableBodyCellStyle}>
                            <div className="row-actions" style={rowActionsStyle}>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                style={{ padding: '8px 12px', fontSize: 13 }}
                                onClick={() => handleLoadConfiguration(configuration)}
                                disabled={
                                  configurationLibraryView === 'trash' ||
                                  !configuration.hasSnapshot ||
                                  isConfigurationActionRunning
                                }
                              >
                                {isRowActionRunning ? 'Opening...' : 'Open'}
                              </button>
                              {configurationLibraryView === 'trash' ? (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ padding: '8px 12px', fontSize: 13 }}
                                    onClick={() =>
                                      handleRestoreConfiguration(configuration)
                                    }
                                    disabled={isConfigurationActionRunning}
                                  >
                                    {configurationAction.id === configuration.id &&
                                    configurationAction.type === 'restore'
                                      ? 'Restoring...'
                                      : 'Restore'}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-danger"
                                    style={{ padding: '8px 12px', fontSize: 13 }}
                                    onClick={() =>
                                      handlePermanentlyDeleteConfiguration(configuration)
                                    }
                                    disabled={isConfigurationActionRunning}
                                  >
                                    {configurationAction.id === configuration.id &&
                                    configurationAction.type === 'delete'
                                      ? 'Deleting...'
                                      : 'Permanently Delete'}
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ padding: '8px 12px', fontSize: 13 }}
                                    onClick={() =>
                                      handleDuplicateConfiguration(configuration)
                                    }
                                    disabled={isConfigurationActionRunning}
                                  >
                                    {configurationAction.id === configuration.id &&
                                    configurationAction.type === 'duplicate'
                                      ? 'Duplicating...'
                                      : 'Duplicate'}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ padding: '8px 12px', fontSize: 13 }}
                                    onClick={() =>
                                      handleSetMainConfiguration(configuration)
                                    }
                                    disabled={
                                      configuration.isMain ||
                                      isConfigurationActionRunning
                                    }
                                  >
                                    {configurationAction.id === configuration.id &&
                                    configurationAction.type === 'main'
                                      ? 'Setting...'
                                      : 'Set Recommended'}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-danger"
                                    style={{ padding: '8px 12px', fontSize: 13 }}
                                    onClick={() =>
                                      handleMoveConfigurationToTrash(configuration)
                                    }
                                    disabled={isConfigurationActionRunning}
                                  >
                                    {configurationAction.id === configuration.id &&
                                    configurationAction.type === 'trash'
                                      ? 'Moving...'
                                      : 'Move to Trash'}
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      {isMixedProduct && !shouldShowConfigurationBuilder ? (
        <section
          className="surface-card surface-card--section product-detail-empty-state"
          style={compactSectionStyle}
        >
          {renderEmptyState(
            'No configuration selected',
            'Open an existing configuration or create a new one to start selecting suppliers.'
          )}
        </section>
      ) : null}

      {shouldShowConfigurationBuilder ? (
        <section
          className="surface-card surface-card--section configuration-builder-panel"
          style={compactSectionStyle}
        >
          <div style={configurationSummaryStyle}>
            <div style={configurationSummaryMainStyle}>
              <h2 style={configurationSummaryTitleStyle}>
                {builderConfigurationMeta?.name || 'New Configuration'}
              </h2>
              <div style={configurationSummaryMetaStyle}>
                {builderCategoryLabels.length > 0 ? renderTags(builderCategoryLabels) : null}
                {builderConfigurationMeta?.recommended ? (
                  <span style={badgeStyle('success')}>Recommended</span>
                ) : null}
                {builderDimensionLabels.length > 0 ? renderTags(builderDimensionLabels) : null}
                {builderCalculationLabels.length > 0 ? renderTags(builderCalculationLabels) : null}
              </div>
              <div style={configurationSummaryMetaStyle}>
                <span style={configurationMetaTextStyle}>
                  {activeProductComponents.length} component{activeProductComponents.length === 1 ? '' : 's'}
                </span>
                <span style={configurationMetaTextStyle}>
                  {selectedSupplierCount} supplier{selectedSupplierCount === 1 ? '' : 's'} selected
                </span>
                <span style={configurationMetaTextStyle}>
                  Updated {formatDate(builderUpdatedAt)}
                </span>
              </div>
            </div>
            {isAdmin ? (
              <div className="section-actions" style={rowActionsStyle}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleOpenEditConfigurationDetails}
                  disabled={isSavingConfiguration || Boolean(configurationAction.type)}
                >
                  Edit Details
                </button>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {(!isMixedProduct || shouldShowConfigurationBuilder) ? (
      <section
        className="surface-card surface-card--section"
        style={isMixedProduct ? compactSectionStyle : undefined}
      >
        <div className="section-header" style={sectionHeaderStyle}>
          <h2 className="section-title">
            {isMixedProduct ? 'Supplier Workspace' : 'Supplier Pricing'}
          </h2>
          {isAdmin && isMixedProduct ? (
            <div className="section-actions" style={rowActionsStyle}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleOpenAddComponentModal}
              >
                + Add Component
              </button>
            </div>
          ) : null}
          {isAdmin && !isMixedProduct ? (
            <div className="section-actions" style={rowActionsStyle}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleOpenAddSupplierModal}
                disabled={availableSuppliersToLink.length === 0}
              >
                + Add Supplier
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setIsManageSuppliersOpen(true)}
                disabled={linkedSupplierManagementRows.length === 0}
              >
                Manage Suppliers
              </button>
            </div>
          ) : null}
        </div>
        {isAdmin && !isMixedProduct && availableSuppliers.length === 0 ? (
          renderEmptyState(
            'No suppliers available',
            'Add a supplier before recording pricing for this product.'
          )
        ) : null}
        {isMixedProduct ? (
          <>
            {builderDimensions.enabled ? (
              <div style={{ ...compactSummaryGridStyle, marginBottom: 16 }}>
                {[
                  ...(builderDimensions.fields || []).map((field) => [
                    getDimensionFieldLabel(field),
                    builderDimensions[field],
                    'm',
                  ]),
                  ...(builderDimensions.area !== null
                    ? [['Area', builderDimensions.area, 'm2']]
                    : []),
                  ...(builderDimensions.volume !== null
                    ? [['Volume', builderDimensions.volume, 'm3']]
                    : []),
                ].map(([label, value, unit]) => (
                  <div key={label} style={compactSummaryItemStyle}>
                    <p style={overviewLabelStyle}>{label}</p>
                    <p style={textStyle}>
                      {formatDimensionValue(value)} {value === null ? '' : unit}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}
            <div style={{ ...configurationSummarySectionStyle, marginBottom: 18 }}>
              <div style={configurationTotalHeaderStyle}>
                <h3 style={componentCardTitleStyle}>Configuration Summary</h3>
                <span style={configurationTotalValueStyle}>
                  {formatPrice(mixedGrandTotal)}
                </span>
              </div>

              <div style={configurationSummaryBlockStyle}>
                <p style={configurationSummaryBlockTitleStyle}>Components</p>
                {mixedComponentPricingRows.length === 0 ? (
                  <p style={helperTextStyle}>No component costs calculated yet.</p>
                ) : (
                  <div style={configurationTotalRowsStyle}>
                    {mixedComponentPricingRows.map((componentRow) => (
                      <div
                        key={componentRow.id || componentRow.name}
                        style={configurationTotalRowStyle}
                      >
                        <span>{componentRow.name || '-'}</span>
                        <strong>{formatPrice(componentRow.lineTotal)}</strong>
                      </div>
                    ))}
                  </div>
                )}
                <div style={configurationSummaryTotalStyle}>
                  <span>Components Total</span>
                  <strong>{formatPrice(mixedComponentSubtotal)}</strong>
                </div>
              </div>

              <div style={configurationSummaryDividerStyle} />

              <div style={configurationSummaryBlockStyle}>
                <p style={configurationSummaryBlockTitleStyle}>Extra Expenses</p>
                {extraExpenses.length === 0 ? (
                  <p style={helperTextStyle}>No extra expenses added.</p>
                ) : (
                  <div style={configurationTotalRowsStyle}>
                    {extraExpenses.map((expense) => (
                      <div
                        key={expense.id || expense.name}
                        style={configurationTotalRowStyle}
                      >
                        <span>{expense.name || '-'}</span>
                        <strong>{formatPrice(expense.amount)}</strong>
                      </div>
                    ))}
                  </div>
                )}
                <div style={configurationSummaryTotalStyle}>
                  <span>Expenses Total</span>
                  <strong>{formatPrice(extraExpensesTotal)}</strong>
                </div>
              </div>

              <div style={configurationSummaryDividerStyle} />

              <div style={configurationSummaryGrandTotalStyle}>
                <span>Grand Total</span>
                <strong>{formatPrice(mixedGrandTotal)}</strong>
              </div>
            </div>
            {activeProductComponents.length === 0 ? (
            renderEmptyState(
              'No components added yet',
              'Add a component to start building this configuration.'
            )
            ) : (() => {
            const expandedComponent = activeProductComponents.find((component) =>
              expandedComponentIds.includes(component.id)
            );
            const rawExpandedSupplierRows = expandedComponent
              ? getConfigurationComponentSupplierRows(
                expandedComponent,
                componentSuppliersByComponentId[expandedComponent.id] || []
              )
              : [];
            const normalizedMixedSupplierSearch = mixedSupplierSearch
              .trim()
              .toLowerCase();
            const expandedSupplierRows = expandedComponent
              ? sortPricingRows(
                rawExpandedSupplierRows.filter((componentSupplier) => {
                  if (!normalizedMixedSupplierSearch) {
                    return true;
                  }

                  return getComponentSupplierDisplayName(
                    expandedComponent,
                    componentSupplier
                  ).toLowerCase().includes(normalizedMixedSupplierSearch);
                }),
                mixedSupplierSort,
                (componentSupplier) =>
                  getComponentSupplierDisplayName(
                    expandedComponent,
                    componentSupplier
                  )
              )
              : [];
            const expandedComponentPricing = expandedComponent
              ? calculateComponentPricing(
                expandedComponent,
                rawExpandedSupplierRows,
                builderDimensions
              )
              : null;
            const isExpandedSupplierLoading = expandedComponent
              ? componentSupplierLoadingByComponentId[expandedComponent.id] === true
              : false;
            const isExpandedSelectionUpdating = expandedComponent
              ? componentSelectionUpdatingByComponentId[expandedComponent.id] === true
              : false;

            return (
              <>
                <div style={supplierSelectionGridStyle}>
                  {activeProductComponents.map((component) => {
                    const componentSupplierRows = getConfigurationComponentSupplierRows(
                      component,
                      componentSuppliersByComponentId[component.id] || []
                    );
                    const componentPricing = calculateComponentPricing(
                      component,
                      componentSupplierRows,
                      builderDimensions
                    );
                    const selectedSupplierName = componentPricing.selectedSupplier
                      ? getComponentSupplierDisplayName(
                        component,
                        componentPricing.selectedSupplier
                      )
                      : '-';
                    const componentStatus =
                      componentStatusLabels[componentPricing.status] ||
                      componentPricing.status;
                    const isExpanded = expandedComponent?.id === component.id;

                    return (
                      <article
                        key={component.id}
                        className="configuration-component-card"
                        style={supplierSelectionCardStyle}
                      >
                        <div style={componentCardHeaderStyle}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <h3 style={componentCardTitleStyle}>
                              {component.name || '-'}
                            </h3>
                            {component.sourceVariantName ? (
                              <p style={componentCardMetaStyle}>
                                Variant: {component.sourceVariantName}
                              </p>
                            ) : null}
                          </div>
                          {isAdmin ? (
                            <details style={componentActionMenuStyle}>
                              <summary
                                aria-label={`Actions for ${component.name || 'component'}`}
                                style={componentActionMenuButtonStyle}
                              >
                                ⋮
                              </summary>
                              <div style={componentActionMenuListStyle}>
                                <button
                                  type="button"
                                  style={componentActionMenuItemStyle}
                                  onClick={(event) => {
                                    event.currentTarget
                                      .closest('details')
                                      ?.removeAttribute('open');
                                    handleOpenEditComponentModal(component);
                                  }}
                                  disabled={isUpdatingComponentStatus}
                                >
                                  Edit Component
                                </button>
                                <button
                                  type="button"
                                  style={{
                                    ...componentActionMenuItemStyle,
                                    color: '#b91c1c',
                                  }}
                                  onClick={(event) => {
                                    event.currentTarget
                                      .closest('details')
                                      ?.removeAttribute('open');
                                    handleSoftDeleteComponent(component);
                                  }}
                                  disabled={isUpdatingComponentStatus}
                                >
                                  Delete Component
                                </button>
                              </div>
                            </details>
                          ) : null}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <div>
                            <p style={overviewLabelStyle}>Selected Supplier</p>
                            <p style={{ ...textStyle, lineHeight: 1.4 }}>
                              {selectedSupplierName}
                            </p>
                          </div>
                          <div>
                            <p style={overviewLabelStyle}>Component Cost</p>
                            <p style={{ ...textStyle, lineHeight: 1.4 }}>
                              {formatPrice(componentPricing.lineTotal)}
                            </p>
                          </div>
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 10,
                            marginTop: 'auto',
                          }}
                        >
                          <span style={componentStatusStyle(componentPricing.status)}>
                            {componentStatus}
                          </span>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ padding: '8px 12px', fontSize: 13 }}
                            onClick={() => handleToggleComponentExpanded(component)}
                          >
                            {isExpanded ? 'Hide Suppliers' : 'Show Suppliers'}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>

                <div
                  className="supplier-workspace-panel"
                  style={supplierWorkspacePanelStyle(Boolean(expandedComponent))}
                >
                  {expandedComponent ? (
                    <div style={supplierWorkspacePanelInnerStyle}>
                      <div style={{ ...sectionHeaderStyle, marginBottom: 10 }}>
                        <div>
                          <h3 style={componentCardTitleStyle}>
                            Suppliers for {expandedComponent.name || 'Component'}
                          </h3>
                          {expandedComponent.sourceVariantName ? (
                            <p style={componentCardMetaStyle}>
                              Variant: {expandedComponent.sourceVariantName}
                            </p>
                          ) : null}
                        </div>
                      </div>

                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                          gap: 12,
                          marginBottom: 12,
                        }}
                      >
                        <SearchBar
                          value={mixedSupplierSearch}
                          onChange={(event) => setMixedSupplierSearch(event.target.value)}
                          placeholder="Search suppliers..."
                        />
                        <label style={fieldWrapStyle}>
                          <span style={labelStyle}>Sort</span>
                          <select
                            name="mixed-supplier-sort"
                            value={mixedSupplierSort}
                            onChange={(event) => setMixedSupplierSort(event.target.value)}
                            style={inputBaseStyle}
                          >
                            {pricingSortOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>

                      {isExpandedSupplierLoading ? (
                        renderEmptyState(
                          'Loading supplier pricing',
                          'Supplier options will appear here shortly.'
                        )
                      ) : expandedSupplierRows.length === 0 ? (
                        renderEmptyState(
                          'No suppliers available',
                          'This component does not have active supplier pricing yet.'
                        )
                      ) : (
                        <div className="table-container" style={tableWrapStyle}>
                          <table
                            className="data-table product-detail-card-table product-detail-component-pricing-table"
                            style={{ ...tableStyle, minWidth: 760 }}
                          >
                            <thead>
                              <tr>
                                <th style={tableHeadCellStyle}>Select</th>
                                <th style={tableHeadCellStyle}>Supplier</th>
                                <th style={tableHeadCellStyle}>Price</th>
                                <th style={tableHeadCellStyle}>Pricing Type</th>
                                <th style={tableHeadCellStyle}>Total</th>
                                <th style={tableHeadCellStyle}>Last Updated</th>
                                <th style={tableHeadCellStyle}>Notes</th>
                                <th style={tableHeadCellStyle}>History</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expandedSupplierRows.map((componentSupplier) => {
                                const hasValidPrice =
                                  hasValidComponentSupplierPrice(componentSupplier);
                                const isSelected =
                                  expandedComponentPricing
                                    ?.selectedComponentSupplierId ===
                                  componentSupplier.id;
                                const isDeletedSupplier = isSupplierDeletedOrMissing(
                                  componentSupplier.supplierId
                                );
                                const isChoiceDisabled =
                                  !hasValidPrice ||
                                  isDeletedSupplier ||
                                  isExpandedSelectionUpdating;
                                const supplierName = getComponentSupplierDisplayName(
                                  expandedComponent,
                                  componentSupplier
                                );
                                const breakdown = getComponentCalculationBreakdown(
                                  expandedComponent,
                                  componentSupplier,
                                  builderDimensions
                                );

                                return (
                                  <tr key={componentSupplier.id} style={supplierRowStyle}>
                                    <td data-label="Select" style={tableBodyCellStyle}>
                                      <input
                                        type="radio"
                                        name={`mixed-supplier-${expandedComponent.id}`}
                                        checked={isSelected}
                                        disabled={isChoiceDisabled}
                                        onChange={() =>
                                          handleSelectComponentSupplier(
                                            expandedComponent,
                                            componentSupplier
                                          )
                                        }
                                        aria-label={`Select ${supplierName} for ${
                                          expandedComponent.name || 'component'
                                        }`}
                                      />
                                    </td>
                                    <td data-label="Supplier" style={tableBodyCellStyle}>{supplierName}</td>
                                    <td data-label="Price" style={tableBodyCellStyle}>
                                      {formatPrice(componentSupplier.currentPrice)}
                                    </td>
                                    <td data-label="Pricing Type" style={tableBodyCellStyle}>
                                      {getPricingMethodLabel(
                                        componentSupplier.pricingMethod
                                      )}
                                    </td>
                                    <td data-label="Total" style={tableBodyCellStyle}>
                                      {formatComponentPricingBreakdown(breakdown)}
                                    </td>
                                    <td data-label="Last Updated" style={tableBodyCellStyle}>
                                      {formatDate(componentSupplier.lastUpdated)}
                                    </td>
                                    <td data-label="Notes" style={tableBodyCellStyle}>
                                      {normalizeHistoryNote(componentSupplier.notes)}
                                    </td>
                                    <td data-label="History" style={tableBodyCellStyle}>
                                      <button
                                        type="button"
                                        className="btn btn-secondary"
                                        style={{ padding: '8px 12px', fontSize: 13 }}
                                        onClick={() =>
                                          handleOpenComponentHistoryModal(
                                            expandedComponent,
                                            componentSupplier
                                          )
                                        }
                                      >
                                        View History
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </>
            );
          })()}
          </>
        ) : isVariantProduct ? (
          <>
            {variantNames.length === 0 ? (
              <p style={textStyle}>No allowed variants defined yet</p>
            ) : (
              <>
                <p style={{ ...helperTextStyle, marginBottom: 12 }}>
                  Select a variant to view supplier prices
                </p>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  <label style={fieldWrapStyle}>
                    <span style={labelStyle}>Variant</span>
                    <select
                      name="variant-selector"
                      value={activeVariantName}
                      onChange={(event) => setSelectedVariantName(event.target.value)}
                      style={inputBaseStyle}
                    >
                      {variantNames.map((variantName) => (
                        <option key={variantName} value={variantName}>
                          {variantName}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label style={fieldWrapStyle}>
                    <span style={labelStyle}>Sort</span>
                    <select
                      name="variant-sort"
                      value={variantSort}
                      onChange={(event) => setVariantSort(event.target.value)}
                      style={inputBaseStyle}
                    >
                      {pricingSortOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </>
            )}

            {variantNames.length > 0 && variantPricingRows.length === 0 ? (
              <p style={textStyle}>No supplier data</p>
            ) : null}

            {variantNames.length > 0 && variantPricingRows.length > 0 ? (
              <div className="table-container" style={tableWrapStyle}>
                <table
                  className="data-table product-detail-card-table product-detail-variant-pricing-table"
                  style={tableStyle}
                >
                  <thead>
                    <tr>
                      <th style={tableHeadCellStyle}>Supplier</th>
                      <th style={tableHeadCellStyle}>Specs</th>
                      <th style={tableHeadCellStyle}>Price</th>
                      <th style={tableHeadCellStyle}>Last Update</th>
                      <th style={tableHeadCellStyle}>Notes</th>
                      <th style={tableHeadCellStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variantPricingRows.map((row) => (
                      <tr key={row.id} style={supplierRowStyle}>
                        <td data-label="Supplier" className="clickable-cell" style={supplierCellStyle}>
                          <button
                            type="button"
                            className="clickable-name"
                            onClick={() => navigate(`/suppliers/${row.supplierId}`)}
                            disabled={row.isMissingSupplier}
                          >
                            {row.supplierName}
                            {row.isTrashed && !row.isMissingSupplier
                              ? ' (Archived Supplier)'
                              : ''}
                          </button>
                        </td>
                        <td data-label="Specs" style={tableBodyCellStyle}>
                          <VariantSpecsPills specs={row.specs} />
                        </td>
                        <td data-label="Price" style={tableBodyCellStyle}>{row.price}</td>
                        <td data-label="Last Update" style={tableBodyCellStyle}>{row.date}</td>
                        <td data-label="Notes" style={tableBodyCellStyle}>{row.note}</td>
                        <td data-label="Actions" style={tableBodyCellStyle}>
                          <div className="row-actions" style={rowActionsStyle}>
                            {isAdmin ? (
                              <button
                                type="button"
                                className="btn btn-secondary"
                                style={{ padding: '8px 12px', fontSize: 13 }}
                                onClick={() => handleOpenAddVariantPriceForRow(row)}
                              >
                                Manage Pricing
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{ padding: '8px 12px', fontSize: 13 }}
                              onClick={() => handleOpenVariantHistoryModal(row)}
                            >
                              View History
                            </button>
                            {isAdmin ? (
                              <button
                                type="button"
                                className="btn btn-danger"
                                style={{
                                  padding: '8px 12px',
                                  fontSize: 13,
                                  opacity: isRemovingSupplier ? 0.6 : 1,
                                  cursor: isRemovingSupplier ? 'not-allowed' : 'pointer',
                                }}
                                onClick={() => handleOpenRemoveModal(row, 'variantSupplier')}
                                disabled={isRemovingSupplier}
                              >
                                Remove Supplier Link
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : supplierPricingRows.length === 0 ? (
          renderEmptyState(
            'No suppliers available',
            'No supplier pricing has been recorded for this product yet.'
          )
        ) : (
          <>
            <div style={{ maxWidth: 260, marginBottom: 16 }}>
              <label style={fieldWrapStyle}>
                <span style={labelStyle}>Sort</span>
                <select
                  name="supplier-pricing-sort"
                  value={supplierPricingSort}
                  onChange={(event) => setSupplierPricingSort(event.target.value)}
                  style={inputBaseStyle}
                >
                  {pricingSortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="table-container" style={tableWrapStyle}>
              <table
                className="data-table product-detail-card-table product-detail-pricing-table"
                style={tableStyle}
              >
              <thead>
                <tr>
                  <th style={tableHeadCellStyle}>Supplier</th>
                  <th style={tableHeadCellStyle}>Price</th>
                  <th style={tableHeadCellStyle}>Last Price Date</th>
                  <th style={tableHeadCellStyle}>Notes</th>
                  <th style={tableHeadCellStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {supplierPricingRows.map((row) => (
                  <tr key={row.id} style={supplierRowStyle}>
                    <td data-label="Supplier" className="clickable-cell" style={supplierCellStyle}>
                      <button
                        type="button"
                        className="clickable-name"
                        onClick={() => navigate(`/suppliers/${row.supplierId}`)}
                        disabled={row.isMissingSupplier}
                      >
                        {row.supplierName}
                        {row.isTrashed && !row.isMissingSupplier
                          ? ' (Archived Supplier)'
                          : ''}
                      </button>
                    </td>
                        <td data-label="Price" style={tableBodyCellStyle}>{row.price}</td>
                        <td data-label="Last Price Date" style={tableBodyCellStyle}>{row.date}</td>
                        <td data-label="Notes" style={tableBodyCellStyle}>{row.note}</td>
                    <td data-label="Actions" style={tableBodyCellStyle}>
                      <div className="row-actions" style={rowActionsStyle}>
                        {isAdmin ? (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ padding: '8px 12px', fontSize: 13 }}
                            onClick={() => handleOpenAddPriceModal(row)}
                          >
                            Manage Pricing
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ padding: '8px 12px', fontSize: 13 }}
                          onClick={() => handleOpenHistoryModal(row.supplierName, row.history)}
                        >
                          View History
                        </button>
                        {isAdmin ? (
                          <button
                            type="button"
                            className="btn btn-danger"
                            style={{
                              padding: '8px 12px',
                              fontSize: 13,
                              opacity: isRemovingSupplier ? 0.6 : 1,
                              cursor: isRemovingSupplier ? 'not-allowed' : 'pointer',
                            }}
                            onClick={() => handleOpenRemoveModal(row)}
                            disabled={isRemovingSupplier}
                          >
                            Remove Supplier Link
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </section>
      ) : null}

      {shouldShowConfigurationBuilder ? (
        <section className="surface-card surface-card--section" style={compactSectionStyle}>
          <div className="section-header" style={sectionHeaderStyle}>
            <h2 className="section-title">Extra Expenses</h2>
            {isAdmin ? (
              <div className="section-actions" style={rowActionsStyle}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleOpenAddExpenseModal}
                >
                  + Add Expense
                </button>
              </div>
            ) : null}
          </div>

          {extraExpenses.length === 0 ? (
            renderEmptyState(
              'No extra expenses added',
              'Add expenses only when this configuration needs costs beyond components.'
            )
          ) : (
            <div className="table-container" style={tableWrapStyle}>
              <table className="data-table" style={{ ...tableStyle, minWidth: 620 }}>
                <thead>
                  <tr>
                    <th style={tableHeadCellStyle}>Expense Name</th>
                    <th style={tableHeadCellStyle}>Expense Value</th>
                    {isAdmin ? <th style={tableHeadCellStyle}>Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {extraExpenses.map((expense) => (
                    <tr key={expense.id} style={supplierRowStyle}>
                      <td style={tableBodyCellStyle}>{expense.name || '-'}</td>
                      <td style={tableBodyCellStyle}>{formatPrice(expense.amount)}</td>
                      {isAdmin ? (
                        <td data-label="Actions" style={tableBodyCellStyle}>
                          <div className="row-actions" style={rowActionsStyle}>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{ padding: '8px 12px', fontSize: 13 }}
                              onClick={() => handleOpenEditExpenseModal(expense)}
                              disabled={isUpdatingExpenseStatus}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger"
                              style={{ padding: '8px 12px', fontSize: 13 }}
                              onClick={() => handleDeleteExpense(expense)}
                              disabled={isUpdatingExpenseStatus}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      {shouldShowConfigurationBuilder && isAdmin ? (
        <section className="surface-card surface-card--section" style={compactSectionStyle}>
          <div className="section-actions" style={rowActionsStyle}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveConfiguration}
              disabled={
                isSavingConfiguration ||
                activeProductComponents.length === 0 ||
                Boolean(configurationAction.type)
              }
            >
              {isSavingConfiguration ? 'Saving...' : 'Save Configuration'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseConfigurationBuilder}
              disabled={isSavingConfiguration || Boolean(configurationAction.type)}
            >
              Close Configuration
            </button>
          </div>
        </section>
      ) : null}

      {(!isMixedProduct || shouldShowConfigurationBuilder) ? (
      <section className="surface-card surface-card--section" style={compactSectionStyle}>
        <button
          type="button"
          className="btn btn-secondary"
          style={{ width: '100%', justifyContent: 'space-between' }}
          aria-expanded={isHistoryExpanded}
          onClick={() => setIsHistoryExpanded((current) => !current)}
        >
          <span>History</span>
          <span aria-hidden="true">{isHistoryExpanded ? 'Collapse' : 'Expand'}</span>
        </button>
        {isHistoryExpanded ? (
          <div style={{ ...overviewGridStyle, marginTop: 16 }}>
            <div style={overviewItemStyle}>
              <p style={overviewLabelStyle}>Last Updated</p>
              <p style={textStyle}>{formatDate(product.updatedAt)}</p>
            </div>
            <div style={overviewItemStyle}>
              <p style={overviewLabelStyle}>Created</p>
              <p style={textStyle}>{formatDate(product.createdAt)}</p>
            </div>
            {isMixedProduct ? (
              <div style={overviewItemStyle}>
                <p style={overviewLabelStyle}>Pricing History</p>
                <p style={textStyle}>
                  Component supplier price history is available from Supplier Workspace.
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>
      ) : null}

      {isAdmin && !isMixedProduct && (
        <FormModal
          isOpen={isAddSupplierOpen}
          onClose={handleCloseAddSupplierModal}
          onSubmit={handleSubmitAddSupplier}
          title="+ Add Supplier"
          description="Create a new supplier link for this product."
          submitLabel={isAddingSupplier ? 'Adding...' : 'Add Supplier'}
          submitDisabled={isAddingSupplier || filteredSuppliersToLink.length === 0}
          cancelDisabled={isAddingSupplier}
          errorMessage={addSupplierError}
        >
          {availableSuppliers.length === 0 ? (
            <p style={helperTextStyle}>
              No suppliers are available for this product yet.
            </p>
          ) : availableSuppliersToLink.length === 0 ? (
            <p style={helperTextStyle}>
              All active suppliers are already linked to this product.
            </p>
          ) : (
            <>
              <div style={modalToolbarStyle}>
                <SearchBar
                  value={addSupplierSearch}
                  onChange={(event) => setAddSupplierSearch(event.target.value)}
                  placeholder="Search suppliers..."
                />
              </div>

              <label style={fieldWrapStyle}>
                <span style={labelStyle}>Supplier *</span>
                <select
                  name="add-supplier"
                  value={selectedSupplierId}
                  onChange={(event) => {
                    setSelectedSupplierId(event.target.value);
                    if (addSupplierError) {
                      setAddSupplierError('');
                    }
                  }}
                  required
                  disabled={isAddingSupplier || filteredSuppliersToLink.length === 0}
                  style={inputBaseStyle}
                >
                  <option value="">Select a supplier</option>
                  {filteredSuppliersToLink.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.companyName}
                    </option>
                  ))}
                </select>
              </label>

              {filteredSuppliersToLink.length === 0 ? (
                <p style={helperTextStyle}>No suppliers match your search.</p>
              ) : null}
            </>
          )}
        </FormModal>
      )}

      {isAdmin && !isMixedProduct && (
        <Modal
          isOpen={isManageSuppliersOpen}
          onClose={handleCloseManageSuppliersModal}
          title="Manage Suppliers"
          description="Review linked suppliers and manage pricing from one place."
          hideFooter
          maxWidth={920}
          contentStyle={historyModalContentStyle}
        >
          {linkedSupplierManagementRows.length === 0 ? (
            <p style={emptyStateStyle}>No linked suppliers for this product.</p>
          ) : (
            <div className="table-container" style={tableWrapStyle}>
              <table
                className="data-table product-detail-card-table product-detail-manage-suppliers-table"
                style={{ ...tableStyle, minWidth: 760 }}
              >
                <thead>
                  <tr>
                    <th style={tableHeadCellStyle}>Supplier</th>
                    <th style={tableHeadCellStyle}>Pricing Type</th>
                    <th style={tableHeadCellStyle}>Latest Price</th>
                    <th style={tableHeadCellStyle}>Last Update</th>
                    <th style={tableHeadCellStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedSupplierManagementRows.map((row) => (
                    <tr key={row.id} style={supplierRowStyle}>
                      <td data-label="Supplier" style={tableBodyCellStyle}>{row.supplierName}</td>
                      <td data-label="Pricing Type" style={tableBodyCellStyle}>{row.pricingMethodLabel}</td>
                      <td data-label="Latest Price" style={tableBodyCellStyle}>{row.price}</td>
                      <td data-label="Last Update" style={tableBodyCellStyle}>{row.date}</td>
                      <td data-label="Actions" style={tableBodyCellStyle}>
                        <div className="row-actions" style={rowActionsStyle}>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ padding: '8px 12px', fontSize: 13 }}
                            onClick={() =>
                              isVariantProduct
                                ? handleOpenAddVariantPriceForRow(row)
                                : handleOpenAddPriceModal(row)
                            }
                          >
                            Manage Pricing
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ padding: '8px 12px', fontSize: 13 }}
                            onClick={() =>
                              isVariantProduct
                                ? handleOpenVariantHistoryModal(row)
                                : handleOpenHistoryModal(row.supplierName, row.history)
                            }
                          >
                            View History
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger"
                            style={{ padding: '8px 12px', fontSize: 13 }}
                            onClick={() =>
                              handleOpenRemoveModal(
                                row,
                                isVariantProduct ? 'variantSupplier' : 'supplier'
                              )
                            }
                            disabled={isRemovingSupplier}
                          >
                            Remove Supplier Link
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}

      {isAdmin && !isMixedProduct && (
        <FormModal
          isOpen={isSupplierPriceModalOpen}
          onClose={handleCloseSupplierPriceModal}
          onSubmit={handleSubmitSupplierPrice}
          title="Manage Pricing"
          submitLabel={isSubmittingSupplierPrice ? 'Saving...' : 'Save Price'}
          submitDisabled={
            isSubmittingSupplierPrice ||
            availableSuppliers.length === 0 ||
            (isVariantProduct && !activeVariantName)
          }
          cancelDisabled={isSubmittingSupplierPrice}
          errorMessage={supplierPriceError}
        >
          {availableSuppliers.length === 0 ? (
            <p style={helperTextStyle}>
              No suppliers are available for this product yet.
            </p>
          ) : (
            <>
              {isVariantProduct ? (
                <div style={fieldWrapStyle}>
                  <span style={labelStyle}>Variant</span>
                  <p style={textStyle}>
                    {activeVariantName || 'No variant selected'}
                  </p>
                </div>
              ) : null}

              <label style={fieldWrapStyle}>
                <span style={labelStyle}>Supplier *</span>
                <select
                  name="supplier"
                  value={selectedSupplierId}
                  onChange={(event) => {
                    setSelectedSupplierId(event.target.value);
                    if (supplierPriceError) {
                      setSupplierPriceError('');
                    }
                  }}
                  required
                  disabled={isSubmittingSupplierPrice || isSupplierPickerLocked}
                  style={inputBaseStyle}
                >
                  <option value="">Select a supplier</option>
                  {availableSuppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.companyName}
                    </option>
                  ))}
                </select>
              </label>

              <label style={fieldWrapStyle}>
                <span style={labelStyle}>Price *</span>
                <input
                  name="price"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={price}
                  onChange={(event) => {
                    setPrice(event.target.value);
                    if (supplierPriceError) {
                      setSupplierPriceError('');
                    }
                  }}
                  required
                  disabled={isSubmittingSupplierPrice}
                  style={inputBaseStyle}
                />
              </label>

              <label style={fieldWrapStyle}>
                <span style={labelStyle}>Pricing Type</span>
                <select
                  name="pricingMethod"
                  value={pricingMethod}
                  onChange={(event) => {
                    setPricingMethod(normalizePricingMethod(event.target.value));
                    if (supplierPriceError) {
                      setSupplierPriceError('');
                    }
                  }}
                  disabled={isSubmittingSupplierPrice}
                  style={inputBaseStyle}
                >
                  {PRICING_METHOD_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label style={fieldWrapStyle}>
                <span style={labelStyle}>Notes</span>
                <textarea
                  name="notes"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  rows={4}
                  disabled={isSubmittingSupplierPrice}
                  style={{
                    ...inputBaseStyle,
                    resize: 'vertical',
                  }}
                />
              </label>
            </>
          )}
        </FormModal>
      )}

      {isAdmin && isMixedProduct && (
        <Modal
          isOpen={isConfigurationModalOpen}
          onClose={handleCloseConfigurationModal}
          title={
            configurationModalMode === 'edit'
              ? 'Edit Configuration Details'
              : 'New Configuration'
          }
          hideFooter
          maxWidth={640}
        >
          <form onSubmit={handleSubmitNewConfiguration} className="modal-form">
            {configurationConflict?.message ? (
              <p className="modal-error">{configurationConflict.message}</p>
            ) : null}

            <label style={fieldWrapStyle}>
              <span style={labelStyle}>Configuration Name *</span>
              <input
                name="configurationName"
                type="text"
                value={configurationForm.name}
                onChange={(event) => {
                  setConfigurationForm((prev) => ({
                    ...prev,
                    name: event.target.value,
                  }));
                  setConfigurationConflict(null);
                }}
                required
                disabled={isSubmittingConfiguration}
                style={inputBaseStyle}
              />
            </label>

            <label style={fieldWrapStyle}>
              <span style={labelStyle}>Category</span>
              <select
                name="configurationCategory"
                value={configurationForm.categoryId}
                onChange={(event) => {
                  setConfigurationForm((prev) => ({
                    ...prev,
                    categoryId: event.target.value,
                  }));
                  setConfigurationConflict(null);
                }}
                disabled={isSubmittingConfiguration}
                style={inputBaseStyle}
              >
                <option value="">Select a category</option>
                {Object.entries(categoryMap)
                  .sort((firstCategory, secondCategory) =>
                    firstCategory[1].localeCompare(secondCategory[1])
                  )
                  .map(([categoryId, categoryName]) => (
                    <option key={categoryId} value={categoryId}>
                      {categoryName}
                    </option>
                  ))}
              </select>
            </label>

            <div style={fieldWrapStyle}>
              <span style={labelStyle}>Dimensions</span>
              {productDimensionFields.length === 0 ? (
                <p style={helperTextStyle}>
                  This mixed product does not require dimensions.
                </p>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: 12,
                  }}
                >
                  {productDimensionFields.map((fieldName) => (
                    <label key={fieldName} style={fieldWrapStyle}>
                      <span style={labelStyle}>
                        {getDimensionFieldLabel(fieldName)} *
                      </span>
                      <input
                        name={`configuration-${fieldName}`}
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={configurationForm.dimensions[fieldName] || ''}
                        onChange={(event) =>
                          handleConfigurationDimensionChange(
                            fieldName,
                            event.target.value
                          )
                        }
                        disabled={isSubmittingConfiguration}
                        style={inputBaseStyle}
                      />
                    </label>
                  ))}
                </div>
              )}
              {(() => {
                const dimensions = normalizeConfigurationDimensions(
                  configurationForm.dimensions,
                  {
                    calculationMode: productCalculationMode,
                    requiredInputs: product.requiredInputs,
                  }
                );
                const calculationLabels = getCalculationSummaryLabels(dimensions);

                return calculationLabels.length > 0 ? (
                  <div style={configurationSummaryMetaStyle}>
                    {calculationLabels.map((label) => (
                      <span key={label} style={configurationMetaTextStyle}>
                        {label}
                      </span>
                    ))}
                  </div>
                ) : null;
              })()}
              {suggestConfigurationName(productName, configurationForm.dimensions, {
                calculationMode: productCalculationMode,
                requiredInputs: product.requiredInputs,
              }) ? (
                <p style={helperTextStyle}>
                  Suggested name: {suggestConfigurationName(
                    productName,
                    configurationForm.dimensions,
                    {
                      calculationMode: productCalculationMode,
                      requiredInputs: product.requiredInputs,
                    }
                  )}
                </p>
              ) : null}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="button"
                onClick={handleCloseConfigurationModal}
                disabled={isSubmittingConfiguration}
              >
                Cancel
              </button>
              {configurationModalMode === 'add' &&
              configurationConflict?.type === 'duplicate' ? (
                <button
                  type="button"
                  className="button button-primary"
                  onClick={handleOpenExistingConfiguration}
                  disabled={isSubmittingConfiguration}
                >
                  Open Existing
                </button>
              ) : (
                <button
                  type="button"
                  className="button button-primary"
                  onClick={handleSubmitNewConfiguration}
                  disabled={isSubmittingConfiguration}
                >
                  {configurationModalMode === 'edit'
                    ? 'Update Details'
                    : isSubmittingConfiguration
                      ? 'Creating...'
                      : 'Create Configuration'}
                </button>
              )}
            </div>
          </form>
        </Modal>
      )}

      {isAdmin && isMixedProduct && (
        <FormModal
          isOpen={isComponentModalOpen}
          onClose={handleCloseComponentModal}
          onSubmit={handleSubmitComponent}
          title={componentModalMode === 'edit' ? 'Edit Component' : 'Add Component'}
          submitLabel={isSubmittingComponent ? 'Saving...' : 'Save Component'}
          submitDisabled={
            isSubmittingComponent ||
            (componentModalMode === 'add' && !componentForm.sourceProductId) ||
            isSourceVariantSelectionMissing
          }
          cancelDisabled={isSubmittingComponent}
          errorMessage={componentError}
        >
          {componentModalMode === 'add' ? (
            <>
              <label style={fieldWrapStyle}>
                <span style={labelStyle}>Search Products *</span>
                <input
                  name="componentProductSearch"
                  type="search"
                  value={componentProductSearch}
                  role="combobox"
                  aria-expanded="true"
                  aria-controls="component-product-results"
                  aria-activedescendant={
                    filteredComponentProductOptions[highlightedComponentProductIndex]
                      ? `component-product-option-${
                        filteredComponentProductOptions[
                          highlightedComponentProductIndex
                        ].id
                      }`
                      : undefined
                  }
                  onChange={(event) => setComponentProductSearch(event.target.value)}
                  onKeyDown={handleComponentProductSearchKeyDown}
                  placeholder="Search products..."
                  disabled={isSubmittingComponent}
                  style={inputBaseStyle}
                />
              </label>

              <div
                id="component-product-results"
                role="listbox"
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  maxHeight: 220,
                  overflowY: 'auto',
                }}
              >
                {filteredComponentProductOptions.length === 0 ? (
                  <p style={{ ...helperTextStyle, padding: 12 }}>
                    No products match this search.
                  </p>
                ) : (
                  filteredComponentProductOptions.map((sourceProduct) => {
                    const isSelected =
                      componentForm.sourceProductId === sourceProduct.id;
                    const isHighlighted =
                      filteredComponentProductOptions[
                        highlightedComponentProductIndex
                      ]?.id === sourceProduct.id;

                    return (
                      <button
                        key={sourceProduct.id}
                        id={`component-product-option-${sourceProduct.id}`}
                        role="option"
                        aria-selected={isSelected}
                        type="button"
                        onClick={() => handleSelectComponentProduct(sourceProduct)}
                        onMouseEnter={() =>
                          setHighlightedComponentProductIndex(
                            filteredComponentProductOptions.findIndex(
                              (item) => item.id === sourceProduct.id
                            )
                          )
                        }
                        disabled={isSubmittingComponent}
                        style={{
                          width: '100%',
                          border: 0,
                          borderBottom: '1px solid #e2e8f0',
                          padding: '8px 12px',
                          textAlign: 'left',
                          backgroundColor:
                            isSelected || isHighlighted ? '#eff6ff' : '#ffffff',
                          color: '#0f172a',
                          cursor: 'pointer',
                          font: 'inherit',
                        }}
                      >
                        {sourceProduct.name || 'Unnamed product'}
                      </button>
                    );
                  })
                )}
              </div>
              {componentForm.sourceProductId ? (
                <p style={helperTextStyle}>
                  Selected component: {componentForm.sourceProductName || componentForm.name}
                  {componentForm.sourceVariantName
                    ? ` / ${componentForm.sourceVariantName}`
                    : ''}
                </p>
              ) : null}
            </>
          ) : (
            <div style={fieldWrapStyle}>
              <span style={labelStyle}>Component</span>
              <p style={textStyle}>{componentForm.name || '-'}</p>
              {componentForm.sourceProductId ? (
                <p style={helperTextStyle}>
                  Linked to product: {componentForm.sourceProductName || componentForm.name}
                </p>
              ) : (
                <p style={helperTextStyle}>
                  Legacy component without a product link.
                </p>
              )}
            </div>
          )}

          {isSelectedSourceVariantProduct ? (
            <label style={fieldWrapStyle}>
              <span style={labelStyle}>Variant *</span>
              <select
                name="componentSourceVariant"
                value={componentForm.sourceVariantName}
                onChange={(event) =>
                  handleSelectComponentVariant(event.target.value)
                }
                required
                disabled={isSubmittingComponent}
                style={inputBaseStyle}
              >
                <option value="">Select a variant</option>
                {sourceVariantOptions.map((variantOption) => (
                  <option key={variantOption.id} value={variantOption.name}>
                    {variantOption.name}
                  </option>
                ))}
                {componentForm.sourceVariantName &&
                !sourceVariantOptions.some(
                  (option) => option.name === componentForm.sourceVariantName
                ) ? (
                  <option value={componentForm.sourceVariantName}>
                    {componentForm.sourceVariantName}
                  </option>
                  ) : null}
              </select>
            </label>
          ) : null}

          <label style={fieldWrapStyle}>
            <span style={labelStyle}>Quantity *</span>
            <input
              name="componentQuantity"
              type="number"
              min="0.01"
              step="0.01"
              value={componentForm.quantity}
              onChange={(event) =>
                handleComponentFormChange('quantity', event.target.value)
              }
              required
              disabled={isSubmittingComponent}
              style={inputBaseStyle}
            />
          </label>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              color: '#0f172a',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            <input
              name="componentRequired"
              type="checkbox"
              checked={componentForm.isRequired}
              onChange={(event) =>
                handleComponentFormChange('isRequired', event.target.checked)
              }
              disabled={isSubmittingComponent}
            />
            Required component
          </label>
        </FormModal>
      )}

      {isAdmin && isMixedProduct && (
        <FormModal
          isOpen={isExpenseModalOpen}
          onClose={handleCloseExpenseModal}
          onSubmit={handleSubmitExpense}
          title={expenseModalMode === 'edit' ? 'Edit Expense' : 'Add Expense'}
          submitLabel={isSubmittingExpense ? 'Saving...' : 'Save Expense'}
          submitDisabled={isSubmittingExpense}
          cancelDisabled={isSubmittingExpense}
          errorMessage={expenseError}
        >
          <label style={fieldWrapStyle}>
            <span style={labelStyle}>Expense Name *</span>
            <input
              name="expenseName"
              type="text"
              value={expenseForm.name}
              onChange={(event) =>
                handleExpenseFormChange('name', event.target.value)
              }
              placeholder="Transportation, Installation, Labor..."
              required
              disabled={isSubmittingExpense}
              style={inputBaseStyle}
            />
          </label>

          <label style={fieldWrapStyle}>
            <span style={labelStyle}>Expense Value *</span>
            <input
              name="expenseValue"
              type="number"
              min="0"
              step="0.01"
              value={expenseForm.amount}
              onChange={(event) =>
                handleExpenseFormChange('amount', event.target.value)
              }
              required
              disabled={isSubmittingExpense}
              style={inputBaseStyle}
            />
          </label>
        </FormModal>
      )}

      {isAdmin && (
        <FormModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onSubmit={handleSubmitEditProduct}
          title="Edit Product"
          submitLabel={isSubmittingEdit ? 'Saving...' : 'Save Product'}
          submitDisabled={isSubmittingEdit}
          cancelDisabled={isSubmittingEdit}
          errorMessage={editError}
        >
          <label style={fieldWrapStyle}>
            <span style={labelStyle}>Name *</span>
            <input
              name="name"
              type="text"
              value={editForm.name}
              onChange={(event) => {
                setEditForm((prev) => ({ ...prev, name: event.target.value }));
                if (editError) {
                  setEditError('');
                }
              }}
              required
              disabled={isSubmittingEdit}
              style={inputBaseStyle}
            />
          </label>

          <label style={fieldWrapStyle}>
            <span style={labelStyle}>Description</span>
            <textarea
              name="description"
              value={editForm.description}
              onChange={(event) => {
                setEditForm((prev) => ({ ...prev, description: event.target.value }));
                if (editError) {
                  setEditError('');
                }
              }}
              rows={4}
              disabled={isSubmittingEdit}
              style={{
                ...inputBaseStyle,
                resize: 'vertical',
              }}
            />
          </label>

          <label style={fieldWrapStyle}>
            <span style={labelStyle}>Product Type</span>
            <select
              name="type"
              value={editForm.type || 'simple'}
              onChange={(event) => {
                setEditForm((prev) => ({
                  ...prev,
                  type: event.target.value,
                  allowedVariants:
                    event.target.value === 'variant'
                      ? normalizeVariantNames(prev.allowedVariants)
                      : [],
                  requiredInputs:
                    event.target.value === 'mixed'
                      ? normalizeMixedProductRequiredInputs(prev.requiredInputs)
                      : [],
                  calculationMode:
                    event.target.value === 'mixed'
                      ? normalizeMixedProductCalculationMode(prev.calculationMode)
                      : 'fixed',
                  variantDraft: '',
                }));
                if (editError) {
                  setEditError('');
                }
              }}
              disabled={isSubmittingEdit}
              style={inputBaseStyle}
            >
              {productTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {(editForm.type || 'simple') === 'variant' ? (
            <div style={fieldWrapStyle}>
              <span style={labelStyle}>Allowed Variants *</span>
              <div className="inline-input-action">
                <input
                  name="allowedVariant"
                  type="text"
                  value={editForm.variantDraft || ''}
                  onChange={(event) => {
                    setEditForm((prev) => ({
                      ...prev,
                      variantDraft: event.target.value,
                    }));
                    if (editError) {
                      setEditError('');
                    }
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleAddAllowedVariant();
                    }
                  }}
                  disabled={isSubmittingEdit}
                  style={inputBaseStyle}
                />
                <button
                  type="button"
                  className="button"
                  onClick={handleAddAllowedVariant}
                  disabled={isSubmittingEdit}
                >
                  Add
                </button>
              </div>
              <ul style={tagsStyle}>
                {normalizeVariantNames(editForm.allowedVariants).length > 0 ? (
                  normalizeVariantNames(editForm.allowedVariants).map((variantName) => (
                    <li key={variantName}>
                      <span className="tag">
                        {variantName}
                        <button
                          type="button"
                          className="button"
                          onClick={() => handleRemoveAllowedVariant(variantName)}
                          disabled={isSubmittingEdit}
                          style={{ marginLeft: 8, padding: '2px 6px' }}
                          aria-label={`Remove ${variantName}`}
                        >
                          Remove
                        </button>
                      </span>
                    </li>
                  ))
                ) : (
                  <li style={helperTextStyle}>No allowed variants yet.</li>
                )}
              </ul>
            </div>
          ) : null}

          {(editForm.type || 'simple') === 'mixed' ? (
            <>
              <label style={fieldWrapStyle}>
                <span style={labelStyle}>Calculation Mode</span>
                <select
                  name="calculationMode"
                  value={normalizeMixedProductCalculationMode(editForm.calculationMode)}
                  onChange={(event) => {
                    const nextCalculationMode =
                      normalizeMixedProductCalculationMode(event.target.value);
                    const requiredModeInputs =
                      getMixedProductCalculationModeRequiredInputs(nextCalculationMode);

                    setEditForm((prev) => ({
                      ...prev,
                      calculationMode: nextCalculationMode,
                      requiredInputs: Array.from(
                        new Set([
                          ...normalizeMixedProductRequiredInputs(prev.requiredInputs),
                          ...requiredModeInputs,
                        ])
                      ),
                    }));
                    if (editError) {
                      setEditError('');
                    }
                  }}
                  disabled={isSubmittingEdit}
                  style={inputBaseStyle}
                >
                  {MIXED_PRODUCT_CALCULATION_MODE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <div style={fieldWrapStyle}>
                <span style={labelStyle}>Required Inputs</span>
                <div style={tagsStyle}>
                  {MIXED_PRODUCT_REQUIRED_INPUT_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className="tag"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                    >
                      <input
                        name={`requiredInput-${option.value}`}
                        type="checkbox"
                        checked={normalizeMixedProductRequiredInputs(
                          editForm.requiredInputs
                        ).includes(option.value)}
                        onChange={() => {
                          setEditForm((prev) => ({
                            ...prev,
                            requiredInputs: toggleRequiredInput(
                              prev.requiredInputs,
                              option.value
                            ),
                          }));
                          if (editError) {
                            setEditError('');
                          }
                        }}
                        disabled={isSubmittingEdit}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>
            </>
          ) : null}

          <div style={fieldWrapStyle}>
            <span style={labelStyle}>Categories</span>
            <CategoryMultiSelect
              value={normalizeIdArray(editForm.categories)}
              onChange={(nextCategories) =>
                {
                  setEditForm((prev) => ({
                    ...prev,
                    categories: normalizeIdArray(nextCategories),
                  }));
                  if (editError) {
                    setEditError('');
                  }
                }
              }
            />
          </div>
        </FormModal>
      )}

      <Modal
        isOpen={isHistoryModalOpen}
        onClose={handleCloseHistoryModal}
        title={selectedSupplierName ? `Price History - ${selectedSupplierName}` : 'Price History'}
        hideFooter
        maxWidth={760}
        contentStyle={historyModalContentStyle}
      >
        {isLoadingHistory ? (
          <p style={emptyStateStyle}>Loading price history...</p>
        ) : selectedHistory.length === 0 ? (
          <p style={emptyStateStyle}>No price history available</p>
        ) : (
          <div className="table-container" style={tableWrapStyle}>
            <table className="data-table" style={tableStyle}>
              <thead>
                <tr>
                  <th style={tableHeadCellStyle}>Date</th>
                  <th style={tableHeadCellStyle}>Price</th>
                  <th style={tableHeadCellStyle}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {selectedHistory.map((entry, index) => (
                  <tr
                    key={`${selectedSupplierName}-${entry?.date?.seconds || entry?.date || 'missing-date'}-${entry?.price || 'missing-price'}-${index}`}
                  >
                    <td style={tableBodyCellStyle}>{formatDate(entry?.date)}</td>
                    <td style={tableBodyCellStyle}>
                      {formatPriceWithPricingMethod(
                        formatPrice(entry?.price),
                        entry?.pricingMethod
                      )}
                    </td>
                    <td style={tableBodyCellStyle}>{normalizeHistoryNote(entry?.note)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isComponentHistoryModalOpen}
        onClose={handleCloseComponentHistoryModal}
        title={
          selectedComponentHistoryTitle
            ? `Component Price History - ${selectedComponentHistoryTitle}`
            : 'Component Price History'
        }
        hideFooter
        maxWidth={760}
        contentStyle={historyModalContentStyle}
      >
        {isLoadingComponentHistory ? (
          <p style={emptyStateStyle}>Loading price history...</p>
        ) : selectedComponentHistory.length === 0 ? (
          <p style={emptyStateStyle}>No price history available</p>
        ) : (
          <div className="table-container" style={tableWrapStyle}>
            <table className="data-table" style={tableStyle}>
              <thead>
                <tr>
                  <th style={tableHeadCellStyle}>Date</th>
                  <th style={tableHeadCellStyle}>Price</th>
                  <th style={tableHeadCellStyle}>Unit</th>
                  <th style={tableHeadCellStyle}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {selectedComponentHistory.map((entry, index) => (
                  <tr
                    key={`${entry?.componentSupplierId || 'component-supplier'}-${entry?.date?.seconds || entry?.date || 'missing-date'}-${entry?.price || 'missing-price'}-${index}`}
                  >
                    <td style={tableBodyCellStyle}>{formatDate(entry?.date)}</td>
                    <td style={tableBodyCellStyle}>
                      {formatPriceWithPricingMethod(
                        formatPrice(entry?.price),
                        entry?.pricingMethod
                      )}
                    </td>
                    <td style={tableBodyCellStyle}>{entry?.unit || ''}</td>
                    <td style={tableBodyCellStyle}>{normalizeHistoryNote(entry?.note)}</td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
        )}
      </Modal>

      <Modal
        isOpen={isRemoveModalOpen}
        onClose={handleCloseRemoveModal}
        onConfirm={handleConfirmRemoveSupplier}
        title="Remove Supplier Link"
        description={
          supplierPendingRemoval?.removalType === 'variantSupplier'
            ? 'Are you sure you want to remove this supplier link and all active variant prices for this supplier from this product?'
            : 'Are you sure you want to remove this supplier link and all its pricing history from this product?'
        }
        confirmLabel={isRemovingSupplier ? 'Removing...' : 'Remove'}
        confirmDisabled={isRemovingSupplier}
        cancelDisabled={isRemovingSupplier}
      />
    </div>
  );
}
